import React, { useState, useEffect } from 'react';
import { getReadableTextColor } from '../utils/colorContrast';
import EditPriceModal from './EditPriceModal';
import CatalogExplorer from './CatalogExplorer';
import { COUNTRIES } from '../utils/postalCodeValidation';
import CuratedOptions from './CuratedOptions';
import { formatCurrency } from '../utils/currency';
import ModalCloseButton from './ui/ModalCloseButton';
import BulkCartExportModal from './BulkCartExportModal';

const normalizeMealTypeLabel = (label) => {
  if (!label) return '';
  const clean = label.trim().toLowerCase();
  if (clean === 'dinner items' || clean === 'dinner' || clean === 'main' || clean === 'main course') {
    return 'Main Course';
  }
  if (clean === 'appetizers' || clean === 'appetizer') {
    return 'Appetizer';
  }
  if (clean === 'desserts' || clean === 'dessert') {
    return 'Dessert';
  }
  if (clean === 'snacks' || clean === 'snack') {
    return 'Snack';
  }
  if (clean === 'drinks' || clean === 'drink' || clean === 'cocktails' || clean === 'cocktail') {
    return 'Drink';
  }
  if (clean === 'cake') {
    return 'Cake';
  }
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getMealType = (item) => {
  if (!item) return 'Other';
  if (item.meal_type) {
    const norm = normalizeMealTypeLabel(item.meal_type);
    if (norm && norm.toLowerCase() !== 'food & cake') {
      return norm;
    }
  }
  // Try to infer from name / description
  const text = `${item.name || ''} ${item.description || ''}`.toLowerCase();
  if (text.includes('dinner') || text.includes('main') || text.includes('entree') || text.includes('entrée') || text.includes('course') || text.includes('platter')) {
    return 'Main Course';
  }
  if (text.includes('appetizer') || text.includes('starter') || text.includes('bite') || text.includes('charcuterie') || text.includes('dip') || text.includes('skewer')) {
    return 'Appetizer';
  }
  if (text.includes('dessert') || text.includes('sweet') || text.includes('chocolate') || text.includes('cookie') || text.includes('cupcake') || text.includes('pastry') || text.includes('tart') || text.includes('pie')) {
    return 'Dessert';
  }
  if (text.includes('cake')) {
    return 'Cake';
  }
  if (text.includes('snack') || text.includes('popcorn') || text.includes('nut') || text.includes('chip') || text.includes('pretzel')) {
    return 'Snack';
  }
  if (text.includes('drink') || text.includes('beverage') || text.includes('cocktail') || text.includes('mocktail') || text.includes('punch') || text.includes('juice') || text.includes('soda')) {
    return 'Drink';
  }
  return 'Other';
};

// Get color name from palette with fallback to hex-to-name conversion
const getColorNameFromPalette = (hex, palette) => {
  if (!hex) return '';

  // Normalize hex: ensure it starts with #
  let normalizedHex = hex.trim();
  if (!normalizedHex.startsWith('#')) {
    normalizedHex = '#' + normalizedHex;
  }
  normalizedHex = normalizedHex.toUpperCase();

  // First try to find in palette
  if (palette && Array.isArray(palette)) {
    const color = palette.find(c => {
      const colorHex = typeof c === 'object' ? c.hex : c;
      if (!colorHex) return false;
      // Normalize palette color hex for comparison
      let normalizedPaletteHex = colorHex.trim();
      if (!normalizedPaletteHex.startsWith('#')) {
        normalizedPaletteHex = '#' + normalizedPaletteHex;
      }
      return normalizedPaletteHex.toUpperCase() === normalizedHex;
    });
    if (color && typeof color === 'object' && color.name) {
      return color.name;
    }
  }

  // Fallback: Convert common hex values to color names
  const colorMap = {
    '#FFFFFF': 'White', '#000000': 'Black', '#FF0000': 'Red', '#00FF00': 'Green',
    '#0000FF': 'Blue', '#FFFF00': 'Yellow', '#FF00FF': 'Magenta', '#00FFFF': 'Cyan',
    '#FFA500': 'Orange', '#800080': 'Purple', '#FFC0CB': 'Pink', '#A52A2A': 'Brown',
    '#808080': 'Gray', '#C0C0C0': 'Silver', '#FFD700': 'Gold', '#CD7F32': 'Bronze',
    '#8B4513': 'Saddle Brown', '#2F4F4F': 'Dark Slate Gray', '#000080': 'Navy',
    '#800000': 'Maroon', '#008000': 'Green', '#FF6347': 'Tomato', '#FF1493': 'Deep Pink',
    '#4169E1': 'Royal Blue', '#32CD32': 'Lime Green', '#FF4500': 'Orange Red',
    '#9370DB': 'Medium Purple', '#FF69B4': 'Hot Pink', '#20B2AA': 'Light Sea Green',
    '#B22222': 'Fire Brick', '#228B22': 'Forest Green', '#4682B4': 'Steel Blue',
    '#DC143C': 'Crimson', '#00CED1': 'Dark Turquoise', '#FF8C00': 'Dark Orange',
    '#9932CC': 'Dark Orchid', '#8B008B': 'Dark Magenta', '#556B2F': 'Dark Olive Green',
    '#2E8B57': 'Sea Green', '#191970': 'Midnight Blue', '#8B0000': 'Dark Red',
    '#4B0082': 'Indigo', '#6A5ACD': 'Slate Blue'
  };

  // Check colorMap with normalized hex
  if (colorMap[normalizedHex]) {
    return colorMap[normalizedHex];
  }

  // If still no match, return empty string (don't use hex in search query)
  return '';
};

const parseInstructionSteps = (instructions) => {
  if (!instructions) return [];
  const text = String(instructions).trim();
  if (!text) return [];

  const numberedSteps = text
    .split(/\s*(?:^|\s)\d+[.)]\s+/)
    .map(step => step.trim())
    .filter(Boolean);

  if (numberedSteps.length > 1) return numberedSteps;

  return text
    .split(/\s*[;•]\s+/)
    .map(step => step.trim())
    .filter(Boolean);
};

const toTitleCaseLabel = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .split(/\s+/)
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

const ACTIVITY_PILL_PALETTE = [
  'bg-stone-100 text-stone-800 border-stone-200',
  'bg-sky-100 text-sky-800 border-sky-200',
  'bg-emerald-100 text-emerald-800 border-emerald-200',
  'bg-amber-100 text-amber-800 border-amber-200',
  'bg-rose-100 text-rose-800 border-rose-200',
  'bg-cyan-100 text-cyan-800 border-cyan-200',
  'bg-indigo-100 text-indigo-800 border-indigo-200',
  'bg-orange-100 text-orange-800 border-orange-200'
];

const getActivityPillClass = (parentActivityNameOrKey) => {
  const key = String(parentActivityNameOrKey || '');
  const hash = key.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return ACTIVITY_PILL_PALETTE[hash % ACTIVITY_PILL_PALETTE.length];
};

const getItemOriginBadge = (item, generatedCategories = []) => {
  if (item?.catalog_type === 'activity_supply') return 'Needed for Activity';
  if (item?.workflow_origin === 'catalog' || item?.origin === 'catalog' || item?.catalog_key || item?.catalog_ref) return 'From Catalog';
  if (item?.source === 'ai' || item?.generated_by === 'ai' || item?.is_ai_generated || item?.origin === 'generated' || item?.workflow_origin === 'generated' || generatedCategories.includes(item?.category)) {
    return 'Generated';
  }
  return 'User Added';
};

const isSameShoppingItem = (candidate, target) => {
  if (!candidate || !target) return false;

  if (target.id && candidate.id === target.id) return true;
  if (target._id && candidate._id === target._id) return true;
  if (target.catalog_key && candidate.catalog_key === target.catalog_key) return true;
  if (target.catalog_ref && candidate.catalog_ref === target.catalog_ref) return true;
  if (target.catalog_id && candidate.catalog_id === target.catalog_id) return true;

  return false;
};

const getLinkedActivities = (item) => {
  if (Array.isArray(item?.linked_activities) && item.linked_activities.length > 0) {
    return item.linked_activities
      .map((link) => ({
        key: String(link?.key || link?.name || '').trim(),
        name: String(link?.name || '').trim(),
        quantity: Number(link?.quantity || 1)
      }))
      .filter((link) => link.key || link.name);
  }

  if (item?.parent_activity_name || item?.parent_activity_key) {
    return [{
      key: String(item?.parent_activity_key || item?.parent_activity_name || '').trim(),
      name: String(item?.parent_activity_name || '').trim(),
      quantity: Number(item?.quantity || 1)
    }];
  }

  return [];
};

const calculateMergedSupplyQuantity = (linkedActivities = [], usageType = 'reusable') => {
  const quantities = linkedActivities
    .map(activity => Number(activity.quantity || 1))
    .filter(q => Number.isFinite(q) && q > 0);

  if (!quantities.length) return 1;

  if (usageType === 'consumable') {
    return quantities.reduce((sum, q) => sum + q, 0);
  }

  return Math.max(...quantities);
};

const mergeActivitySupplyItem = (existing, incoming) => {
  const existingLinks = getLinkedActivities(existing);
  const incomingLinks = getLinkedActivities(incoming);
  const linkMap = new Map();

  [...existingLinks, ...incomingLinks].forEach(link => {
    const key = String(link.key || link.name || '').trim();
    if (!key) return;
    linkMap.set(key, {
      key,
      name: link.name,
      quantity: Number(link.quantity || 1)
    });
  });

  const linked_activities = Array.from(linkMap.values());
  const supply_usage_type = incoming.supply_usage_type || existing.supply_usage_type || 'reusable';
  const quantity = calculateMergedSupplyQuantity(linked_activities, supply_usage_type);
  const activityNames = linked_activities.map(link => link.name).filter(Boolean);

  return {
    ...existing,
    ...incoming,
    linked_activities,
    supply_usage_type,
    quantity,
    description: activityNames.length ? `Needed for ${activityNames.join(', ')}.` : incoming.description || existing.description,
    quantity_reason: activityNames.length ? `Needed for ${activityNames.join(', ')}` : incoming.quantity_reason || existing.quantity_reason,
    parent_activity_key: undefined,
    parent_activity_name: undefined,
    parent_activity_ref: undefined,
    parent_activity_id: undefined
  };
};

const detachActivityFromSupplyItem = (item, parent) => {
  const parentKeys = [
    parent?.catalog_key,
    parent?.catalog_ref,
    parent?.catalog_id,
    parent?.id,
    parent?._id,
    parent?.name
  ].filter(Boolean).map(String);
  const parentName = String(parent?.name || '');
  const supply_usage_type = item?.supply_usage_type || 'reusable';
  const linked_activities = getLinkedActivities(item).filter((link) => {
    const linkKey = String(link.key || '');
    const linkName = String(link.name || '');
    return !parentKeys.includes(linkKey) && !(parentName && linkName === parentName);
  });

  if (!linked_activities.length) return null;

  const activityNames = linked_activities.map(link => link.name).filter(Boolean);

  return {
    ...item,
    linked_activities,
    quantity: calculateMergedSupplyQuantity(linked_activities, supply_usage_type),
    description: activityNames.length ? `Needed for ${activityNames.join(', ')}.` : item.description,
    quantity_reason: activityNames.length ? `Needed for ${activityNames.join(', ')}` : item.quantity_reason,
    parent_activity_key: linked_activities.length === 1 ? linked_activities[0].key : undefined,
    parent_activity_name: linked_activities.length === 1 ? linked_activities[0].name : undefined,
    parent_activity_ref: undefined,
    parent_activity_id: undefined
  };
};

const mergeCatalogItemsIntoList = (prevItems, bundleItems) => {
  const nextItems = [...prevItems];

  bundleItems.forEach((incomingItem) => {
    if (!incomingItem) return;

    if (incomingItem.catalog_type === 'activity_supply') {
      const incomingSupplyKey = incomingItem.supply_key || incomingItem.catalog_key;
      const existingIndex = nextItems.findIndex((item) =>
        item?.catalog_type === 'activity_supply'
        && (item?.supply_key === incomingSupplyKey || item?.catalog_key === incomingSupplyKey)
      );

      if (existingIndex >= 0) {
        nextItems[existingIndex] = mergeActivitySupplyItem(nextItems[existingIndex], incomingItem);
      } else {
        nextItems.push(incomingItem);
      }
      return;
    }

    const alreadyExists = nextItems.some((item) => (
      (incomingItem?.catalog_key && item?.catalog_key === incomingItem.catalog_key)
      || (!incomingItem?.catalog_key && item?.id === incomingItem.id)
    ));

    if (!alreadyExists) {
      nextItems.push(incomingItem);
    }
  });

  return nextItems;
};

const isActivitySupplyLinkedToParent = (candidate, parent) => {
  if (!candidate || !parent) return false;

  const candidateType = candidate.catalog_type || candidate.catalogType;
  const parentKeys = [
    parent.catalog_key,
    parent.catalog_ref,
    parent.catalog_id,
    parent.id,
    parent._id,
    parent.name
  ].filter(Boolean).map(String);

  const childParentKeys = [
    candidate.parent_activity_key,
    candidate.parent_activity_ref,
    candidate.parent_activity_id,
    candidate.parent_activity_name
  ].filter(Boolean).map(String);
  const linkedActivityKeys = getLinkedActivities(candidate)
    .flatMap((link) => [link.key, link.name])
    .filter(Boolean)
    .map(String);

  const directKeyMatch = childParentKeys.some(key => parentKeys.includes(key)) || linkedActivityKeys.some(key => parentKeys.includes(key));
  const nameMatch = candidate.parent_activity_name && parent.name && String(candidate.parent_activity_name) === String(parent.name);
  const descriptionMatch =
    candidateType === "activity_supply" &&
    parent.name &&
    (
      String(candidate.description || "").includes(parent.name) ||
      String(candidate.quantity_reason || "").includes(parent.name)
    );

  return candidateType === "activity_supply" && (directKeyMatch || nameMatch || descriptionMatch);
};

const shouldRemoveCatalogBundleItem = (item, catalogItem, candidateKey) => {
  return (
    (candidateKey && item?.catalog_key === candidateKey) ||
    (item?.name === catalogItem?.name && item?.category === catalogItem?.category) ||
    isActivitySupplyLinkedToParent(item, catalogItem)
  );
};

const ShoppingList = ({ theme, eventData, items, setItems, onShare, planId, previousView = 'dashboard', sharePermissions = null, isSharedView = false, inTab = false, expandedCategories: externalExpandedCategories, setExpandedCategories: setExternalExpandedCategories, showCuratedOptions = false, onItemLinksChanged }) => {
  const [customCategories, setCustomCategories] = useState([]);
  const [lastCatalogTab, setLastCatalogTab] = useState('items');
  const [lastCatalogCategory, setLastCatalogCategory] = useState('Decor');

  // Mobile detection for consistent padding
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Internal state for expandedCategories (fallback if not provided by parent)
  const [internalExpandedCategories, setInternalExpandedCategories] = useState(() => {
    // Default: all categories collapsed, only first category expanded
    const categories = Object.keys(theme?.categories || {});
    const init = {};
    categories.forEach((c, idx) => { init[c] = idx === 0; }); // Only first expanded
    return init;
  });

  // Use external state if provided, otherwise use internal state
  const expandedCategories = externalExpandedCategories !== undefined ? externalExpandedCategories : internalExpandedCategories;
  const setExpandedCategories = setExternalExpandedCategories || setInternalExpandedCategories;
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showBulkCartModal, setShowBulkCartModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogLaunch, setCatalogLaunch] = useState({ initialTab: 'items', initialCategory: '', initialSearch: '' });
  const [showAddLinkModal, setShowAddLinkModal] = useState(false);
  const [selectedItemForLink, setSelectedItemForLink] = useState(null);
  const [showEditPriceModal, setShowEditPriceModal] = useState(false);
  const [selectedItemForPriceEdit, setSelectedItemForPriceEdit] = useState(null);
  const [selectedItemIdForPriceEdit, setSelectedItemIdForPriceEdit] = useState(null);
  const [showStoreOptionsModal, setShowStoreOptionsModal] = useState(false);
  const [selectedItemForStore, setSelectedItemForStore] = useState(null);
  const [showBuyOnlineModal, setShowBuyOnlineModal] = useState(false);
  const [selectedItemForBuyOnline, setSelectedItemForBuyOnline] = useState(null);
  const [showFoodSourcingModal, setShowFoodSourcingModal] = useState(false);
  const [selectedFoodItem, setSelectedFoodItem] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedItemForPurchase, setSelectedItemForPurchase] = useState(null);
  const [pendingRemoveItemId, setPendingRemoveItemId] = useState(null);
  const [pendingRemoveLinkedSupplies, setPendingRemoveLinkedSupplies] = useState([]);
  const [purchaseForm, setPurchaseForm] = useState({
    status: 'purchased',
    source: 'online',
    vendor: '',
    actualPrice: '',
    feedback: 'not_sure',
    notes: ''
  });
  const [showQuantityModal, setShowQuantityModal] = useState(false);
  const [quantityModalItem, setQuantityModalItem] = useState(null);
  const [quantityInput, setQuantityInput] = useState('');
  const [newItem, setNewItem] = useState({
    category: '',
    name: '',
    description: '',
    purchaseType: 'buy_only', // buy_only, rent_only, buy_or_rent
    buy_price: '',
    rent_price: '',
    quantity: '1',
    unit: ''
  });
  const [newCategory, setNewCategory] = useState('');
  const [newLink, setNewLink] = useState({ url: '', label: '' });

  const zipCode = eventData?.zip_code || '';
  const country = eventData?.country || 'US';

  // Helper to get Amazon domain based on country
  const getAmazonDomain = (countryCode) => {
    const domainMap = {
      'US': 'amazon.com',
      'AU': 'amazon.com.au',
      'NZ': 'amazon.com.au', // New Zealand uses Amazon Australia
      'UK': 'amazon.co.uk',
      'GB': 'amazon.co.uk', // Great Britain
      'CA': 'amazon.ca',
      'DE': 'amazon.de',
      'FR': 'amazon.fr',
      'IT': 'amazon.it',
      'ES': 'amazon.es',
      'NL': 'amazon.nl',
      'BE': 'amazon.nl', // Belgium uses Amazon Netherlands
      'AT': 'amazon.de', // Austria uses Amazon Germany
      'CH': 'amazon.de', // Switzerland uses Amazon Germany
      'SE': 'amazon.se',
      'NO': 'amazon.no',
      'DK': 'amazon.de', // Denmark uses Amazon Germany
      'FI': 'amazon.de', // Finland uses Amazon Germany
      'PL': 'amazon.de', // Poland uses Amazon Germany
      'IE': 'amazon.co.uk', // Ireland uses Amazon UK
      'PT': 'amazon.es', // Portugal uses Amazon Spain
      'JP': 'amazon.co.jp',
      'IN': 'amazon.in',
      'BR': 'amazon.com.br',
      'MX': 'amazon.com.mx',
      'SG': 'amazon.sg',
      'AE': 'amazon.ae',
      'SA': 'amazon.sa',
      'TR': 'amazon.com.tr',
      'CN': 'amazon.cn',
    };
    return domainMap[countryCode?.toUpperCase()] || 'amazon.com'; // Default to US
  };

  // Helper to build location text with country context
  const buildLocationText = (includeNear = true) => {
    const countryName = COUNTRIES.find(c => c.code === country)?.name || '';
    const prefix = includeNear ? ' near ' : '';
    if (zipCode) {
      return countryName ? `${prefix}${zipCode}, ${countryName}` : `${prefix}${zipCode}`;
    }
    return countryName ? `${prefix}${countryName}` : (includeNear ? ' near me' : 'me');
  };

  const getItemDisplayName = (item) => item?.name || item?.item_name || '';

  const isFoodItem = (item) => {
    const text = `${getItemDisplayName(item)} ${item?.item_name || ''} ${item?.category || ''} ${item?.food_type || ''}`.toLowerCase();
    return [
      'food', 'cake', 'dessert', 'cupcake', 'cookie', 'meal', 'dinner', 'lunch', 'brunch',
      'breakfast', 'appetizer', 'drink', 'beverage', 'cocktail', 'mocktail', 'catering',
      'platter', 'charcuterie', 'salad', 'pizza', 'taco', 'bbq', 'sandwich', 'bakery',
      'restaurant', 'menu'
    ].some(keyword => text.includes(keyword));
  };

  const isDessertFoodItem = (item) => {
    const text = `${getItemDisplayName(item)} ${item?.item_name || ''} ${item?.category || ''} ${item?.food_type || ''}`.toLowerCase();
    return ['cake', 'dessert', 'cupcake', 'cookie', 'tart', 'pie', 'bakery', 'pastry', 'sweet'].some(keyword => text.includes(keyword));
  };

  const getFoodSearchName = (item) => {
    const rawName = (getItemDisplayName(item) || item?.item_name || '').trim();
    if (!rawName) return 'party food';
    // Do not run food through extractItemType: "farm-to-table" is a food style,
    // not a cue to search for furniture tables.
    return rawName.replace(/\s+/g, ' ');
  };

  const getFoodSourceSelections = () => {
    const sources = eventData?.food_source;
    if (Array.isArray(sources)) return sources.map(src => String(src).toLowerCase());
    if (typeof sources === 'string' && sources) return [sources.toLowerCase()];
    return [];
  };

  const getFoodSourcingOptions = (item) => {
    const sources = getFoodSourceSelections();
    const hasPreference = sources.length > 0;
    const options = [];
    const add = (id, title, description, tone = 'emerald') => {
      if (!options.some(option => option.id === id)) {
        options.push({ id, title, description, tone });
      }
    };

    if (!hasPreference || sources.some(src => ['delivery', 'takeout'].includes(src))) {
      add('restaurants', 'Restaurants & delivery', 'Find nearby restaurants that sell, deliver, or offer pickup for this item.', 'emerald');
    }
    if (!hasPreference || sources.includes('catering')) {
      add('caterers', 'Caterers', 'Best for platters, dinner parties, larger guest counts, or staffed service.', 'orange');
    }
    if (sources.includes('cooking')) {
      add('groceries', 'Groceries nearby', 'Find grocery or specialty markets for ingredients if you plan to cook.', 'blue');
    }
    if (sources.includes('potluck')) {
      add('web', 'Recipe and potluck ideas', 'Search for make-ahead ideas guests can bring or you can assign.', 'gray');
    }
    if (isDessertFoodItem(item)) {
      add('bakery', 'Bakery or dessert shop', 'Best for cakes, cupcakes, cookies, dessert trays, and custom sweets.', 'pink');
    }
    add('web', 'Search the web', 'Use a normal Google search when you want broader ideas first.', 'gray');

    return options;
  };

  const isFurnitureOrRentalItem = (item) => {
    const text = `${getItemDisplayName(item)} ${item?.item_name || ''} ${item?.category || ''}`.toLowerCase();
    return ['table', 'chair', 'linen', 'tent', 'heater', 'cooler', 'serveware', 'serving station'].some(keyword => text.includes(keyword));
  };

  // Parse price string to number
  const parsePrice = (priceStr) => {
    if (priceStr === null || priceStr === undefined) return 0;
    const n = parseFloat(String(priceStr).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };

  // Format price. Preserve cents for user-entered purchase prices, but keep whole-dollar
  // estimates clean when the plan data is already rounded.
  const formatPrice = (amount) => {
    const numericAmount = Number(amount || 0);
    const hasCents = Math.abs(numericAmount % 1) > 0.001;
    return formatCurrency(numericAmount, eventData?.country || 'US', {
      decimals: hasCents ? 2 : 0
    });
  };
  const formatEstimatedPrice = (amount) => `Est. ${formatPrice(amount)}`;

  const hasPricingEvidence = (item) => {
    if (!item) return false;
    if (Array.isArray(item.shortlistedLinks) && item.shortlistedLinks.length > 0) return true;
    if (Array.isArray(item.links) && item.links.length > 0) return true;
    if (Array.isArray(item.sourcing_channels) && item.sourcing_channels.length > 0) return true;
    if (item.purchase_url || item.buy_url || item.source_url) return true;
    if (item.vendor || item.purchase_vendor || item.brand || item.estimate_source === 'User Added' || item.estimate_source === 'user') return true;
    if (item.origin === 'user' || item.is_custom === true || item.isCustom === true || item.userAdded === true) return true;
    if (typeof getItemOriginBadge === 'function' && getItemOriginBadge(item) === 'User Added') return true;
    return false;
  };

  const displayEstimatedPrice = (price, item = null) => {
    const parsed = parsePrice(price);
    if (price === null || price === undefined || isNaN(parsed) || parsed <= 0) {
      return 'Estimate needed';
    }
    if (parsed <= 1) {
      if (item && !hasPricingEvidence(item)) {
        return 'Estimate needed';
      }
    }
    return `Est. ${formatPrice(parsed)}`;
  };

  // Calculate category totals
  // Note: buy_price and rent_price are already totals (accounting for quantity), so we don't multiply by quantity
  const calculateCategoryTotals = (categoryItems) => {
    let buyTotal = 0;
    let optimizedTotal = 0;
    let hasRentable = false;

    categoryItems.forEach(item => {
      if (!item.purchased) {
        // Activity supply costs are included in the parent activity estimate — exclude to prevent double-counting.
        if (item.catalog_type === 'activity_supply') return;
        // Price is already the total for the item (includes quantity), so use it directly
        const buyPrice = parsePrice(item.buy_price);
        buyTotal += buyPrice;

        if (item.rentable && item.rent_price) {
          hasRentable = true;
          optimizedTotal += parsePrice(item.rent_price);
        } else {
          optimizedTotal += buyPrice;
        }
      }
    });

    return { buyTotal, optimizedTotal, hasRentable };
  };

  // Calculate grand totals
  // Note: buy_price and rent_price are already totals (accounting for quantity), so we don't multiply by quantity
  const calculateGrandTotals = () => {
    let buyTotal = 0;
    let optimizedTotal = 0;

    items.forEach(item => {
      if (!item.purchased) {
        // Activity supply costs are included in the parent activity estimate — exclude to prevent double-counting.
        if (item.catalog_type === 'activity_supply') return;
        // Price is already the total for the item (includes quantity), so use it directly
        const buyPrice = parsePrice(item.buy_price);
        buyTotal += buyPrice;

        if (item.rentable && item.rent_price) {
          optimizedTotal += parsePrice(item.rent_price);
        } else {
          optimizedTotal += buyPrice;
        }
      }
    });

    return { buyTotal, optimizedTotal };
  };

  // Check if user has edit permissions
  const canEditShoppingList = !isSharedView || (sharePermissions && sharePermissions.can_edit_shopping_list);
  const handleTogglePurchased = (itemId) => {
    if (!canEditShoppingList) return; // View-only: don't allow editing
    const item = items.find(candidate => candidate.id === itemId);
    if (!item) return;

    if (item.purchased) {
      setItems(prev => prev.map(candidate =>
        candidate.id === itemId ? { ...candidate, purchased: false } : candidate
      ));
      return;
    }

    setSelectedItemForPurchase(item);
    setPurchaseForm({
      status: 'purchased',
      source: isFoodItem(item) ? 'local_vendor' : 'online',
      vendor: '',
      actualPrice: parsePrice(item.buy_price || item.rent_price) ? String(parsePrice(item.buy_price || item.rent_price)) : '',
      feedback: 'not_sure',
      notes: ''
    });
    setShowPurchaseModal(true);
  };

  const handleSavePurchaseDetails = () => {
    if (!selectedItemForPurchase) return;
    setItems(prev => prev.map(item => {
      if (item.id !== selectedItemForPurchase.id) return item;
      return {
        ...item,
        purchased: true,
        purchase_status: purchaseForm.status,
        purchase_source: purchaseForm.source,
        purchase_vendor: purchaseForm.vendor.trim(),
        actual_price: purchaseForm.actualPrice ? formatPrice(parsePrice(purchaseForm.actualPrice)) : '',
        actual_price_value: purchaseForm.actualPrice ? parsePrice(purchaseForm.actualPrice) : null,
        purchase_feedback: purchaseForm.feedback,
        purchase_notes: purchaseForm.notes.trim(),
        purchased_at: new Date().toISOString()
      };
    }));
    setShowPurchaseModal(false);
    setSelectedItemForPurchase(null);
  };

  const handleRemoveItem = (itemId) => {
    if (!canEditShoppingList) return; // View-only: don't allow editing
    const parentItem = items.find(item => item.id === itemId);
    if (
      parentItem &&
      (parentItem.catalog_type === 'activity' || parentItem.category === 'Activities')
    ) {
      setPendingRemoveLinkedSupplies(
        items.filter(candidate => isActivitySupplyLinkedToParent(candidate, parentItem))
      );
    } else {
      setPendingRemoveLinkedSupplies([]);
    }
    setPendingRemoveItemId(itemId);
  };

  const confirmRemoveItem = () => {
    if (!pendingRemoveItemId) return;
    setItems(prevItems => {
      const parentItem = prevItems.find(i => i.id === pendingRemoveItemId);
      if (!parentItem) {
        return prevItems.filter(i => i.id !== pendingRemoveItemId);
      }

      const shouldRemoveLinkedSupplies =
        parentItem.catalog_type === 'activity' ||
        parentItem.category === 'Activities' ||
        (parentItem.workflow_origin === 'catalog' && parentItem.category === 'Activities');

      return prevItems.map((candidate) => {
        if (isSameShoppingItem(candidate, parentItem)) return null;
        if (
          shouldRemoveLinkedSupplies &&
          isActivitySupplyLinkedToParent(candidate, parentItem)
        ) {
          return detachActivityFromSupplyItem(candidate, parentItem);
        }
        return candidate;
      }).filter(Boolean);
    });
    setPendingRemoveItemId(null);
    setPendingRemoveLinkedSupplies([]);
  };

  const updateItemQuantity = (targetItem, nextQty) => {
    if (!canEditShoppingList || !targetItem) return;
    const qty = parseInt(nextQty, 10);
    if (isNaN(qty) || qty < 1) {
      alert('Please enter a valid quantity (minimum 1)');
      return;
    }

    const itemId = targetItem.id;
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        // Get the original per-unit price (either from stored original or calculate from current)
        let pricePerUnit = 0;
        let rentPricePerUnit = 0;

        if (item.originalBuyPricePerUnit) {
          // Use stored original per-unit price
          pricePerUnit = parsePrice(item.originalBuyPricePerUnit);
        } else {
          // Calculate from current price and quantity (fallback for old items)
          const currentQty = item.quantity || 1;
          pricePerUnit = parsePrice(item.buy_price) / currentQty;
        }

        if (item.originalRentPricePerUnit) {
          rentPricePerUnit = parsePrice(item.originalRentPricePerUnit);
        } else if (item.rent_price) {
          const currentQty = item.quantity || 1;
          rentPricePerUnit = parsePrice(item.rent_price) / currentQty;
        }

        // Adjust prices based on new quantity
        let updatedItem = {
          ...item,
          quantity: qty,
          quantityManuallyEdited: true
        };

        // Calculate new total buy price
        if (pricePerUnit > 0) {
          updatedItem.buy_price = `$${Math.round(pricePerUnit * qty)}`;
        }

        // Calculate new total rent price
        if (rentPricePerUnit > 0) {
          updatedItem.rent_price = `$${Math.round(rentPricePerUnit * qty)}`;
        }

        return updatedItem;
      }
      return item;
    }));
  };

  const handleQuantityEdit = (item) => {
    if (!canEditShoppingList) return;
    setQuantityModalItem(item);
    setQuantityInput(String(item.quantity || 1));
    setShowQuantityModal(true);
  };

  const handleQuantityIncrease = () => {
    const current = parseInt(quantityInput, 10) || 1;
    setQuantityInput(String(current + 1));
  };

  const handleQuantityDecrease = () => {
    const current = parseInt(quantityInput, 10) || 1;
    if (current > 1) {
      setQuantityInput(String(current - 1));
    }
  };

  const handleQuantitySave = () => {
    if (!canEditShoppingList || !quantityModalItem) return;
    const qty = parseInt(quantityInput, 10);
    updateItemQuantity(quantityModalItem, qty);
    setShowQuantityModal(false);
    setQuantityModalItem(null);
    setQuantityInput('');
  };

  const handleQuantityModalClose = () => {
    setShowQuantityModal(false);
    setQuantityModalItem(null);
    setQuantityInput('');
  };

  // Parse price to number for search
  const getPriceForSearch = (priceStr) => {
    if (!priceStr) return null;
    const price = parsePrice(priceStr);
    return price > 0 ? Math.round(price) : null;
  };

  // Helper to extract core item type from item name (for in-store searches)
  const extractItemType = (item) => {
    if (!item) return '';

    const fullName = (item.name || item.item_name || '').toLowerCase();
    // For tables, extract the core type
    if (fullName.includes('table') || fullName.includes('picnic table')) {
      // Extract table type (e.g., "picnic table", "dining table", "round table")
      if (fullName.includes('picnic')) return 'picnic tables';
      if (fullName.includes('dining')) return 'dining tables';
      if (fullName.includes('round')) return 'round tables';
      if (fullName.includes('rectangular')) return 'rectangular tables';
      return 'tables';
    }

    // For other items, use item_name if available, otherwise extract first 2-3 words
    if (item.item_name) {
      return item.item_name.toLowerCase();
    }

    // Extract first 2-3 meaningful words
    const words = fullName.split(/\s+/).filter(w =>
      !['rustic', 'wooden', 'whimsical', 'elegant', 'vintage', 'modern', 'decorative'].includes(w)
    );
    return words.slice(0, 3).join(' ');
  };

  // Helper to calculate table seating capacity needed
  const calculateTableSeating = (guestCount) => {
    if (!guestCount || guestCount <= 0) return null;

    // Estimate number of tables needed (6-8 guests per table is standard)
    const guestsPerTable = 7; // Average
    const numTables = Math.ceil(guestCount / guestsPerTable);

    // Determine appropriate table size
    // For picnic tables: common sizes are 6, 8, 10, 12 seater
    // Choose size that minimizes number of tables while fitting guests
    let tableSize;
    if (numTables === 1) {
      // Single table - choose size that fits all guests
      if (guestCount <= 6) tableSize = 6;
      else if (guestCount <= 8) tableSize = 8;
      else if (guestCount <= 10) tableSize = 10;
      else tableSize = 12;
    } else {
      // Multiple tables - use 8-10 seater as standard
      const avgGuestsPerTable = guestCount / numTables;
      if (avgGuestsPerTable <= 6) tableSize = 6;
      else if (avgGuestsPerTable <= 8) tableSize = 8;
      else if (avgGuestsPerTable <= 10) tableSize = 10;
      else tableSize = 12;
    }

    return { numTables, tableSize };
  };

  // Build search query from item (helper function)
  const buildSearchQuery = (item, mode = 'online') => {
    if (!item) return '';

    const fullName = item.name || item.item_name || '';
    const itemNameLower = fullName.toLowerCase();

    // For in-store searches, use generic item type with "for sale" or "store" to find stores, not locations
    if (mode === 'store') {
      const itemType = extractItemType(item);
      // Add "for sale" or "store" to make it clear we're looking for stores that sell the item
      return `${itemType} for sale`;
    }

    // For online and rental searches, use descriptive name
    // Extract key words from full name (first 3-4 words capture the theme)
    const nameWords = fullName.trim().split(/\s+/);
    let searchQuery = nameWords.slice(0, 4).join(' ');

    // For tables, add seating capacity if we can calculate it
    // Preserve the table type (picnic, dining, etc.) when adding seating
    if ((itemNameLower.includes('table') || itemNameLower.includes('picnic table')) && eventData?.guest_count) {
      const tableInfo = calculateTableSeating(eventData.guest_count);
      if (tableInfo) {
        // Replace generic "table" with specific type + seating
        if (itemNameLower.includes('picnic')) {
          searchQuery = searchQuery.replace(/table[s]?/i, `${tableInfo.tableSize} seater picnic table`);
        } else if (itemNameLower.includes('dining')) {
          searchQuery = searchQuery.replace(/table[s]?/i, `${tableInfo.tableSize} seater dining table`);
        } else {
          searchQuery = searchQuery.replace(/table[s]?/i, `${tableInfo.tableSize} seater table`);
        }
      }
    }

    // Get color name - prioritize item_color, then color_tag from palette
    let colorName = '';
    const itemColor = item.item_color || '';

    if (itemColor) {
      // Check if itemColor is a hex value - if so, convert to color name
      let actualColorName = itemColor;
      if (itemColor.startsWith('#')) {
        // It's a hex value, convert to color name using the same function
        const palette = theme?.color_palette || [];
        actualColorName = getColorNameFromPalette(itemColor, palette);
      }

      // Only use if it's a valid color name (not hex, not empty)
      if (actualColorName && actualColorName.trim() && !actualColorName.startsWith('#')) {
        const ambiguousColors = ['navy', 'army', 'marine', 'air force'];
        const colorLower = actualColorName.toLowerCase();
        if (!ambiguousColors.some(amb => colorLower.includes(amb))) {
          colorName = actualColorName.trim();
        }
      }
    } else if (item.color_tag) {
      // Always get color name from palette (not hex)
      const palette = theme?.color_palette || [];
      const paletteColor = getColorNameFromPalette(item.color_tag, palette);

      // Ensure we have a valid color name (not empty, not hex)
      if (paletteColor && paletteColor.trim() && !paletteColor.startsWith('#')) {
        const ambiguousColors = ['navy', 'army', 'marine', 'air force'];
        const colorLower = paletteColor.toLowerCase().trim();
        if (colorLower && !ambiguousColors.some(amb => colorLower.includes(amb))) {
          colorName = paletteColor.trim();
        }
      } else {
        // If paletteColor is empty but we have a color_tag, try direct colorMap lookup as fallback
        if (!paletteColor && item.color_tag) {
          const normalizedHex = item.color_tag.startsWith('#')
            ? item.color_tag.toUpperCase()
            : '#' + item.color_tag.toUpperCase();
          const directColorMap = {
            '#FFD700': 'Gold', '#FFA500': 'Orange', '#FF0000': 'Red', '#00FF00': 'Green',
            '#0000FF': 'Blue', '#FFFF00': 'Yellow', '#800080': 'Purple', '#FFC0CB': 'Pink',
            '#808080': 'Gray', '#C0C0C0': 'Silver', '#000000': 'Black', '#FFFFFF': 'White'
          };
          if (directColorMap[normalizedHex]) {
            colorName = directColorMap[normalizedHex];
          }
        }
      }
    }

    // Add color if we have a safe one (never add hex values)
    // Only add color for certain item types where color is searchable
    const colorRelevantItems = ['balloon', 'banner', 'tablecloth', 'napkin', 'plate', 'cup', 'decor'];
    const isColorRelevant = colorRelevantItems.some(type => itemNameLower.includes(type));
    if (colorName && colorName.trim() && !colorName.startsWith('#') && isColorRelevant) {
      searchQuery += ` ${colorName.trim()}`;
    }

    // Add price constraint for online purchases (always include buy_price)
    if (mode === 'online') {
      const price = getPriceForSearch(item.buy_price);
      if (price) {
        searchQuery += ` under $${price}`;
      }
    }

    return searchQuery;
  };

  // Determine if an item should show custom/ready-made modal
  // Items that make sense for customization: banners, invitations, place cards, some centerpieces
  // Standard products (string lights, plates, etc.) should go directly to search
  const shouldShowCustomModal = (item, mode = 'online') => {
    if (!item) return false;

    // Show a choice for online purchases without forcing users into Shopping-only results.
    if (mode === 'online') {
      return true;
    }

    // For store mode, keep the existing logic
    const itemName = (item.name || item.item_name || '').toLowerCase();
    const category = (item.category || '').toLowerCase();

    // Items that typically support customization
    const customizableKeywords = [
      'banner', 'invitation', 'place card', 'placecard', 'name card',
      'centerpiece', 'sign', 'poster', 'printable', 'label', 'tag',
      'badge', 'certificate', 'program', 'menu', 'favor tag'
    ];

    // Categories that typically have customizable items
    const customizableCategories = ['printables', 'decor'];

    // Check if item name contains customizable keywords
    const hasCustomizableKeyword = customizableKeywords.some(keyword =>
      itemName.includes(keyword)
    );

    // Check if it's in a category that often has customizable items
    const isCustomizableCategory = customizableCategories.includes(category);

    // Items that are ALWAYS standard (never show modal)
    const standardProductKeywords = [
      'string light', 'fairy light', 'led light', 'lighting',
      'plate', 'cup', 'napkin', 'utensil', 'fork', 'knife', 'spoon',
      'balloon', 'table', 'chair', 'linen', 'tablecloth', 'rental',
      'food', 'cake', 'drink', 'beverage', 'appetizer', 'dessert',
      'game', 'activity', 'toy', 'prize'
    ];

    const isStandardProduct = standardProductKeywords.some(keyword =>
      itemName.includes(keyword)
    );

    // Show modal only if it has customizable keywords AND is not a standard product
    return (hasCustomizableKeyword || (isCustomizableCategory && !isStandardProduct)) && !isStandardProduct;
  };

  const handleBuy = (item, mode = 'online') => {
    if (isFoodItem(item)) {
      setSelectedFoodItem(item);
      setShowFoodSourcingModal(true);
      return;
    }

    // Check if this item should show the custom/ready-made modal
    const showModal = shouldShowCustomModal(item, mode);

    if (mode === 'store') {
      if (showModal) {
        // Show modal to ask about customization vs ready-to-buy
        setSelectedItemForStore(item);
        setShowStoreOptionsModal(true);
      } else {
        // Go directly to store search (ready-made)
        const baseSearch = isFurnitureOrRentalItem(item)
          ? `event rental ${extractItemType(item)}`
          : buildSearchQuery(item, 'store');
        const nearText = buildLocationText(true);
        const storeQuery = encodeURIComponent(`${baseSearch}${nearText}`);
        const searchUrl = `https://www.google.com/maps/search/${storeQuery}`;
        window.open(searchUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    // Online mode - always show modal
    setSelectedItemForBuyOnline(item);
    setShowBuyOnlineModal(true);
  };

  const handleBuyOnlineOption = (option) => {
    if (!selectedItemForBuyOnline) return;

    // Build search query using helper function
    const searchQuery = buildSearchQuery(selectedItemForBuyOnline, 'online');
    const encodedQuery = encodeURIComponent(searchQuery);

    if (option === 'google') {
      // General Google search usually gives better results than forcing Shopping.
      const query = encodeURIComponent(`${searchQuery} online`);
      const searchUrl = `https://www.google.com/search?q=${query}`;
      window.open(searchUrl, '_blank', 'noopener,noreferrer');
    } else if (option === 'amazon') {
      // Amazon with country-specific domain
      const amazonDomain = getAmazonDomain(country);
      const searchUrl = `https://www.${amazonDomain}/s?k=${encodedQuery}`;
      window.open(searchUrl, '_blank', 'noopener,noreferrer');
    }

    setShowBuyOnlineModal(false);
    setSelectedItemForBuyOnline(null);
  };

  const handleStoreOption = (option) => {
    if (!selectedItemForStore) return;

    const itemType = extractItemType(selectedItemForStore);
    const itemName = (selectedItemForStore.name || selectedItemForStore.item_name || '').toLowerCase();
    const nearText = buildLocationText(true);
    let searchPhrase = isFurnitureOrRentalItem(selectedItemForStore)
      ? `event rental ${itemType}`
      : `${itemType} party supplies`;

    if (option === 'custom') {
      if (itemName.includes('cake') || itemName.includes('dessert')) {
        searchPhrase = `custom ${itemType} bakery`;
      } else if (
        itemName.includes('banner')
        || itemName.includes('sign')
        || itemName.includes('poster')
        || itemName.includes('printable')
        || itemName.includes('invitation')
        || itemName.includes('menu')
        || itemName.includes('place card')
        || itemName.includes('wall art')
        || itemName.includes('art print')
        || itemName.includes('backdrop')
        || itemName.includes('photo wall')
        || itemName.includes('foam board')
        || itemName.includes('canvas')
        || itemName.includes('vinyl')
      ) {
        searchPhrase = `${itemType} custom print shop`;
      } else if (itemName.includes('centerpiece') || itemName.includes('flower') || itemName.includes('floral')) {
        searchPhrase = `custom ${itemType} florist`;
      } else {
        searchPhrase = `custom ${itemType} event vendor`;
      }
    }

    const storeQuery = encodeURIComponent(`${searchPhrase}${nearText}`);
    const searchUrl = `https://www.google.com/maps/search/${storeQuery}`;
    window.open(searchUrl, '_blank', 'noopener,noreferrer');

    setShowStoreOptionsModal(false);
    setSelectedItemForStore(null);
  };

  const handleRent = (item) => {
    if (!item) return;

    const fullName = item.name || item.item_name || '';
    const itemNameLower = fullName.toLowerCase();

    // Start with "party rentals" or "event rentals" for better rental company results
    let searchQuery = 'party rentals';

    // Extract core item type (remove descriptive words)
    let itemType = '';
    if (item.item_name) {
      itemType = item.item_name.toLowerCase();
    } else {
      // Extract first 2-3 meaningful words, removing theme/descriptive words
      const words = fullName.split(/\s+/).filter(w =>
        !['rustic', 'wooden', 'whimsical', 'elegant', 'vintage', 'modern', 'decorative', 'whimsical', 'soiree'].includes(w.toLowerCase())
      );
      itemType = words.slice(0, 3).join(' ');
    }

    // For tables, add seating capacity while preserving table type
    if ((itemNameLower.includes('table') || itemNameLower.includes('picnic table')) && eventData?.guest_count) {
      const tableInfo = calculateTableSeating(eventData.guest_count);
      if (tableInfo) {
        if (itemNameLower.includes('picnic')) {
          itemType = itemType.replace(/table[s]?/i, `${tableInfo.tableSize} seater picnic table`);
        } else if (itemNameLower.includes('dining')) {
          itemType = itemType.replace(/table[s]?/i, `${tableInfo.tableSize} seater dining table`);
        } else {
          itemType = itemType.replace(/table[s]?/i, `${tableInfo.tableSize} seater table`);
        }
      }
    }

    searchQuery += ` ${itemType}`;

    // Add color only if very relevant (balloons, linens, etc.)
    let colorName = '';
    if (item.color_tag) {
      const palette = theme?.color_palette || [];
      const paletteColor = getColorNameFromPalette(item.color_tag, palette);
      if (paletteColor && paletteColor.trim() && !paletteColor.startsWith('#')) {
        const ambiguousColors = ['navy', 'army', 'marine', 'air force'];
        const colorLower = paletteColor.toLowerCase().trim();
        if (colorLower && !ambiguousColors.some(amb => colorLower.includes(amb))) {
          const colorRelevantItems = ['balloon', 'tablecloth', 'linen', 'napkin', 'chair cover'];
          if (colorRelevantItems.some(type => itemNameLower.includes(type))) {
            colorName = paletteColor.trim();
          }
        }
      }
    }

    if (colorName) {
      searchQuery += ` ${colorName}`;
    }

    // Add price if available (use rent_price, not buy_price)
    // Only for smaller items where price filtering helps
    const isLargeItem = itemNameLower.includes('table') ||
      itemNameLower.includes('chair') ||
      itemNameLower.includes('furniture');
    const price = getPriceForSearch(item.rent_price);
    if (price && !isLargeItem && price < 500) {
      searchQuery += ` under $${price}`;
    }

    // Add location
    const locationText = buildLocationText(false);

    // Rentals work better in Maps because users need local availability and delivery.
    const rentalQuery = encodeURIComponent(`${searchQuery}${locationText ? ` near ${locationText}` : ''}`);
    const searchUrl = `https://www.google.com/maps/search/${rentalQuery}`;
    window.open(searchUrl, '_blank', 'noopener,noreferrer');
  };

  const handleFoodSourcingOption = (option) => {
    if (!selectedFoodItem) return;
    const itemName = getFoodSearchName(selectedFoodItem);
    const locationText = buildLocationText(true);
    let query = `${itemName} restaurant delivery${locationText}`;

    if (option === 'caterers') {
      query = `${itemName} catering${locationText}`;
    } else if (option === 'bakery') {
      query = `${itemName} bakery${locationText}`;
    } else if (option === 'groceries') {
      query = `${itemName} ingredients grocery${locationText}`;
    } else if (option === 'restaurants') {
      query = `${itemName} restaurant delivery pickup${locationText}`;
    } else if (option === 'web') {
      query = `order ${itemName} delivery ${zipCode || COUNTRIES.find(c => c.code === country)?.name || ''}`.trim();
      window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
      setShowFoodSourcingModal(false);
      setSelectedFoodItem(null);
      return;
    }

    window.open(`https://www.google.com/maps/search/${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
    setShowFoodSourcingModal(false);
    setSelectedFoodItem(null);
  };

  const openCatalogForCategory = (category) => {
    const normalizedCategory = String(category || '').toLowerCase();
    const isActivityCategory = normalizedCategory.includes('activit');
    const categorySearchMap = {
      food: 'party food dessert drinks serving',
      cake: 'cake dessert bakery',
      drink: 'drinks beverages',
      rental: 'rentals tables chairs linens',
      decor: 'decor centerpiece backdrop balloons',
      supply: 'plates cups napkins tableware',
      favor: 'party favors favor bags'
    };
    const matchedSearch = Object.entries(categorySearchMap).find(([key]) => normalizedCategory.includes(key))?.[1] || '';
    const shouldUseSearchOnly = Boolean(matchedSearch);
    setCatalogLaunch({
      initialTab: isActivityCategory ? 'activities' : 'items',
      initialCategory: isActivityCategory || shouldUseSearchOnly ? '' : category,
      initialSearch: isActivityCategory ? '' : matchedSearch
    });
    setShowCatalogModal(true);
  };

  // Catering flow intentionally disabled for now (per product decision)
  // const handleFindCatering = (...)

  // Pick a sensible vendor hint + button label based on the user's food source choice.
  // Examples:
  //  - food_source=takeout  -> "Find restaurant", search "{item} restaurant pickup near {zip}"
  //  - food_source=catering -> "Find caterer",    search "{item} caterer near {zip}"
  //  - food_source=delivery -> "Find delivery",   search "{item} delivery near {zip}"
  //  - cake items always force "bakery"
  const getVendorHintForItem = (item) => {
    const name = String(item?.name || '').toLowerCase();
    // Match by NAME only — categories like "Food & Cake" used to make every
    // food item look like a bakery item. Use specific bakery keywords.
    const bakeryRegex = /\b(cake|cupcake|cup\s*cakes?|pastry|pastries|cookie|cookies|tart|cheesecake|brownie|brownies|macaron|macarons|donut|doughnut|muffin|muffins|pie|pies|scone|scones|croissant|croissants)\b/i;
    if (bakeryRegex.test(name)) {
      return { hint: 'bakery', label: 'Find bakery', emoji: '🎂' };
    }
    // Beverages branch — soda, juices, cocktails, etc. typically come from a
    // grocery store or liquor store rather than a restaurant or bakery.
    const beverageRegex = /\b(soda|sodas|drink|drinks|cocktail|cocktails|wine|beer|champagne|prosecco|liquor|spirits|juice|juices|water|sparkling\s*water|coffee|tea|mocktail|mocktails|punch|lemonade|smoothie|smoothies)\b/i;
    if (beverageRegex.test(name)) {
      return { hint: 'liquor store or grocery', label: 'Find beverages', emoji: '🍹' };
    }
    const sources = Array.isArray(eventData?.food_source) ? eventData.food_source : [];
    if (sources.includes('catering')) {
      return { hint: 'caterer', label: 'Find caterer', emoji: '🍽️' };
    }
    if (sources.includes('takeout')) {
      return { hint: 'restaurant pickup', label: 'Find restaurant', emoji: '🥡' };
    }
    if (sources.includes('delivery')) {
      return { hint: 'delivery', label: 'Find delivery', emoji: '🛵' };
    }
    // Safe default: anything edible that isn't bakery / beverages becomes a
    // restaurant search — never falls back to bakery.
    return { hint: 'restaurant', label: 'Find restaurant', emoji: '🍽️' };
  };

  const handleOrderFromVendor = (item) => {
    // For food items, intentionally use the raw display name rather than buildSearchQuery
    // which can append irrelevant budget/decor data and produce useless results.
    const rawName = String(item?.name || item?.item_name || '').trim();
    const queryName = rawName || (buildSearchQuery(item, 'online') || '').trim();
    const { hint } = getVendorHintForItem(item);
    const locationHint = zipCode ? ` near ${zipCode}` : '';
    const searchQuery = encodeURIComponent(`${queryName} ${hint}${locationHint}`.trim());
    const searchUrl = `https://www.google.com/search?q=${searchQuery}`;
    window.open(searchUrl, '_blank', 'noopener,noreferrer');
  };

  const handleReplace = (item) => {
    alert(`Replace functionality coming soon!\n\nThis would show AI-suggested alternatives for: ${item.name}`);
  };

  const isValidPrice = (val) => {
    if (val === null || val === undefined) return true;
    const str = String(val).trim();
    if (str === '') return true;
    const priceRegex = /^\$?\s*\d+(\.\d+)?$/;
    return priceRegex.test(str);
  };

  const isValidQuantity = (val) => {
    if (val === null || val === undefined) return false;
    const str = String(val).trim();
    const qtyRegex = /^[1-9]\d*$/;
    return qtyRegex.test(str);
  };

  const canAddNewItem = Boolean(
    newItem.name?.trim() &&
    newItem.category?.trim() &&
    isValidQuantity(newItem.quantity) &&
    (newItem.purchaseType === 'rent_only' || isValidPrice(newItem.buy_price)) &&
    (newItem.purchaseType === 'buy_only' || isValidPrice(newItem.rent_price))
  );

  // Add new item
  const handleAddItem = () => {
    if (!canAddNewItem) return;

    const qty = Math.max(1, Number(newItem.quantity) || 1);
    const unit = newItem.unit ? newItem.unit.trim() : '';

    const rawBuyPrice = newItem.purchaseType !== 'rent_only' ? (newItem.buy_price || null) : null;
    const rawRentPrice = newItem.purchaseType !== 'buy_only' ? (newItem.rent_price || null) : null;

    let buyPriceStr = null;
    let rentPriceStr = null;
    let originalBuyPricePerUnit = null;
    let originalRentPricePerUnit = null;

    if (rawBuyPrice !== null) {
      const parsedBuy = parsePrice(rawBuyPrice);
      if (parsedBuy > 0) {
        originalBuyPricePerUnit = `$${Math.round(parsedBuy / qty)}`;
        buyPriceStr = `$${parsedBuy}`;
      }
    }

    if (rawRentPrice !== null) {
      const parsedRent = parsePrice(rawRentPrice);
      if (parsedRent > 0) {
        originalRentPricePerUnit = `$${Math.round(parsedRent / qty)}`;
        rentPriceStr = `$${parsedRent}`;
      }
    }

    const newItemObj = {
      id: `custom-${Date.now()}`,
      category: newItem.category,
      name: newItem.name,
      description: newItem.description,
      quantity: qty,
      unit: unit,
      buy_price: buyPriceStr,
      rent_price: rentPriceStr,
      originalBuyPricePerUnit: originalBuyPricePerUnit,
      originalRentPricePerUnit: originalRentPricePerUnit,
      rentable: newItem.purchaseType !== 'buy_only',
      priority: 'nice-to-have',
      purchased: false,
      shortlistedLinks: [],
      isCustom: true,
      workflow_origin: 'user',
      origin: 'user'
    };

    setItems(prev => [...prev, newItemObj]);
    setNewItem({
      category: '',
      name: '',
      description: '',
      purchaseType: 'buy_only',
      buy_price: '',
      rent_price: '',
      quantity: '1',
      unit: ''
    });
    setShowAddItemModal(false);
  };

  // Add new category
  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      alert('Please enter a category name');
      return;
    }
    if (allCategories.includes(newCategory.trim())) {
      alert('Category already exists');
      return;
    }
    setCustomCategories(prev => [...prev, newCategory.trim()]);
    setNewCategory('');
    setShowAddCategoryModal(false);
  };

  // Handle catalog item selection
const handleSelectItemFromCatalog = (catalogItem) => {
  if (!canEditShoppingList) return false;

  // Check if item already exists
  const candidateKey = catalogItem.catalog_key || `${catalogItem.catalog_type || 'item'}:${String(catalogItem.catalog_id || catalogItem.name || '').toLowerCase()}`;
  const companionItems = Array.isArray(catalogItem?.companion_items) ? catalogItem.companion_items : [];
  const bundleItems = [catalogItem, ...companionItems].filter(Boolean);
  const matchesCatalogItem = (item) =>
    (candidateKey && item.catalog_key === candidateKey) ||
    (item.name === catalogItem.name && item.category === catalogItem.category) ||
    (catalogItem._id && item.id?.includes(catalogItem._id));
  const exists = items.some(item => matchesCatalogItem(item));

  if (exists) {
    setItems(prev => {
      const parentItem = prev.find(item => matchesCatalogItem(item));
      if (!parentItem) {
        return prev.filter(item => !matchesCatalogItem(item));
      }
      const shouldRemoveLinkedSupplies =
        parentItem.catalog_type === 'activity' ||
        parentItem.category === 'Activities' ||
        (parentItem.workflow_origin === 'catalog' && parentItem.category === 'Activities');

      return prev.map((candidate) => {
        if (isSameShoppingItem(candidate, parentItem)) return null;
        if (
          shouldRemoveLinkedSupplies &&
          isActivitySupplyLinkedToParent(candidate, parentItem)
        ) {
          return detachActivityFromSupplyItem(candidate, parentItem);
        }
        return candidate;
      }).filter(Boolean);
    });
    return true;
  }

  // Add item bundle to shopping list
  setItems(prev => mergeCatalogItemsIntoList(prev, bundleItems));
  return true;
  // Don't close modal automatically - let them shop more
  // setShowCatalogModal(false);
};

  // Add link to item
  const handleAddLink = async (closeAfterAdd = false) => {
    if (!newLink.url) {
      alert('Please enter a URL');
      return;
    }

    // When no custom label was provided, generate a sequential default like
    // "Link 1", "Link 2", ... based on the count of existing links for the item.
    const trimmedLabel = String(newLink.label || '').trim();
    const existingLinkCount = Array.isArray(selectedItemForLink?.shortlistedLinks)
      ? selectedItemForLink.shortlistedLinks.length
      : 0;
    const generatedLabel = trimmedLabel || `Link ${existingLinkCount + 1}`;

    const linkToAdd = {
      url: newLink.url,
      label: generatedLabel
    };

    let updatedItem = null;
    setItems(prev => prev.map(item => {
      if (item.id === selectedItemForLink.id) {
        const next = {
          ...item,
          shortlistedLinks: [...(item.shortlistedLinks || []), linkToAdd]
        };
        updatedItem = next;
        return next;
      }
      return item;
    }));

    setSelectedItemForLink((prev) => ({
      ...prev,
      shortlistedLinks: [...(prev?.shortlistedLinks || []), linkToAdd]
    }));
    setNewLink({ url: '', label: '' });

    if (typeof onItemLinksChanged === 'function' && updatedItem) {
      // Notify the parent so sibling theme plans for the same event can
      // inherit this new link for any matching item (same catalog_key or
      // same name+category).
      try {
        onItemLinksChanged(updatedItem, updatedItem.shortlistedLinks || []);
      } catch (_) {}
    }

    if (closeAfterAdd) {
      setShowAddLinkModal(false);
      setSelectedItemForLink(null);
    }
  };

  // Remove link from item
  const handleRemoveLink = (itemId, linkIndex) => {
    let updatedItem = null;
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const newLinks = [...(item.shortlistedLinks || [])];
        newLinks.splice(linkIndex, 1);
        const next = { ...item, shortlistedLinks: newLinks };
        updatedItem = next;
        return next;
      }
      return item;
    }));
    if (typeof onItemLinksChanged === 'function' && updatedItem) {
      try {
        onItemLinksChanged(updatedItem, updatedItem.shortlistedLinks || []);
      } catch (_) {}
    }
  };

  const openEditPrice = (item) => {
    setSelectedItemForPriceEdit(item);
    setSelectedItemIdForPriceEdit(item.id);
    setShowEditPriceModal(true);
  };

  const applyPriceEdit = (nextPrices) => {
    setItems(prev => prev.map(item => {
      if (item.id !== selectedItemIdForPriceEdit) return item;
      return {
        ...item,
        buy_price: nextPrices.buy_price,
        rent_price: item.rentable ? nextPrices.rent_price : null,
        isPriceEdited: true
      };
    }));

    setShowEditPriceModal(false);
    setSelectedItemForPriceEdit(null);
    setSelectedItemIdForPriceEdit(null);
  };

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  // Get all categories (theme + custom + any real item categories already in the list)
  const themeCategories = Object.keys(theme.categories);
  const groupedCategories = Object.keys(groupedItems);
  const allCategories = [...new Set([...themeCategories, ...customCategories, ...groupedCategories])];
  const generatedCategorySet = new Set(themeCategories);
  const getCategoryOriginMeta = (category, categoryItems) => {
    if (category === 'Activity Supplies') {
      return { label: 'Needed for Activities', className: 'bg-stone-100 text-zinc-800' };
    }
    if (generatedCategorySet.has(category)) {
      return { label: 'Generated', className: 'bg-gray-100 text-gray-700' };
    }
    if ((categoryItems || []).some(item => item?.workflow_origin === 'catalog' || item?.origin === 'catalog' || item?.catalog_key || item?.catalog_ref)) {
      return { label: 'From Catalog', className: 'bg-sky-100 text-sky-700' };
    }
    return { label: 'User Added', className: 'bg-amber-100 text-amber-800' };
  };

  // Category emojis
  const categoryEmojis = {
    'Decor': '🎨',
    'Food & Cake': '🍰',
    'Activities': '🎮',
    'Printables': '📄',
    'Rentals': '🏠',
    'Games': '🎲'
  };

  const grandTotals = calculateGrandTotals();
  const purchasedItems = items.filter(item => item.purchased);
  const unpurchasedCount = items.filter(item => !item.purchased).length;

  const hasUnestimatedUnpurchasedItems = items.some(item => {
    if (item.purchased) return false;
    const price = item.rentable && item.rent_price ? item.rent_price : item.buy_price;
    const parsed = parsePrice(price);
    if (price === null || price === undefined || isNaN(parsed) || parsed <= 0) {
      return true;
    }
    if (parsed <= 1 && !hasPricingEvidence(item)) {
      return true;
    }
    return false;
  });

  // Check if all categories are expanded
  const allExpanded = allCategories.every(cat => expandedCategories[cat] === true);
  // Toggle all categories
  const handleToggleAll = () => {
    const shouldExpand = !allExpanded;
    const newState = {};
    allCategories.forEach(cat => {
      newState[cat] = shouldExpand;
    });
    setExpandedCategories(newState);
  };

  // Stepper is now sticky (in flow), need enough space to clear stepper height (~60-80px)
  // When inTab is true, remove outer padding and wrapper styling
  const outerWrapperClass = inTab
    ? ''
    : `max-w-5xl mx-auto animate-slide-up ${isMobile ? 'pt-20' : 'pt-6 sm:pt-8'} relative bg-gray-50`;
  const outerWrapperStyle = inTab ? {} : { zIndex: 10, isolation: 'isolate', position: 'relative' };

  const mealSelections = Array.isArray(eventData?.meals) ? eventData.meals : [];
  const wantsCocktails = mealSelections.some((meal) => String(meal).toLowerCase().includes('cocktail'));
  const hasBeverageItems = (items || []).some((item) => {
    const text = `${item?.name || ''} ${item?.category || ''} ${item?.description || ''}`.toLowerCase();
    return /cocktail|mocktail|beverage|bar|drink|spirit|wine|beer/.test(text);
  });

  const content = (
    <>
      {/* Main Card */}
      <div className={`${inTab ? '' : 'bg-white rounded-xl shadow-lg'} p-4 sm:p-6 md:p-10`}>
        {!inTab && (
          <div className="mb-4">
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 text-center">
              Shopping List
            </h1>
          </div>
        )}

        {/* Grand Total Summary */}
        <div className="bg-stone-50 border border-stone-200 rounded-2xl p-4 sm:p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
            <div className="text-left">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-stone-400 font-sans mb-0.5">
                Shopping List Summary
              </span>
              <h3 className="text-lg font-extrabold text-gray-900 font-sans leading-snug">
                Estimated Grand Total
              </h3>
              <p className="text-xs font-semibold text-gray-500 mt-0.5">{unpurchasedCount} items remaining</p>
            </div>
            
            <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center gap-4 border-t sm:border-t-0 border-stone-200/70 pt-3.5 sm:pt-0 w-full sm:w-auto shrink-0">
              {grandTotals.buyTotal !== grandTotals.optimizedTotal ? (
                <>
                  <div className="text-left sm:text-right">
                    <span className="block text-[9px] font-bold uppercase tracking-wider text-amber-600 mb-0.5 font-sans">
                      Rent-Optimized
                    </span>
                    <div className="text-xl sm:text-2xl font-black text-amber-700 leading-none">
                      {formatEstimatedPrice(grandTotals.optimizedTotal)}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <span className="block text-[9px] font-bold uppercase tracking-wider text-green-500 mb-0.5 font-sans">
                      Buy All
                    </span>
                    <div className="text-base sm:text-lg font-extrabold text-green-600 leading-none">
                      {formatEstimatedPrice(grandTotals.buyTotal)}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-left sm:text-right w-full sm:w-auto flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-center">
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-green-500 sm:mb-1 font-sans">
                    Total Estimate
                  </span>
                  <div className="text-2xl sm:text-3xl font-black text-green-600 leading-none">
                    {formatEstimatedPrice(grandTotals.buyTotal)}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Budget status */}
          {eventData?.budget && (
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-sm text-gray-700">
                Budget: <span className="font-semibold">{formatPrice(eventData.budget)}</span>
                {typeof eventData.budget_tolerance_percent === 'number' && (
                  <span className="text-gray-500"> ({eventData.budget_tolerance_percent === 0 ? '0% tolerance' : `±${eventData.budget_tolerance_percent}% tolerance`})</span>
                )}
              </div>
              {(() => {
                // Use the provided tolerance value, or default to 10% if not set
                // Note: 0 is a valid number, so we check for null/undefined explicitly
                const tol = (eventData.budget_tolerance_percent !== null && eventData.budget_tolerance_percent !== undefined)
                  ? eventData.budget_tolerance_percent
                  : 10;
                const upper = eventData.budget * (1 + tol / 100);
                const overBy = grandTotals.optimizedTotal - upper;
                if (overBy > 1) {
                  return (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-100 text-red-800 text-sm font-semibold">
                      Est. over budget by {formatPrice(overBy)}
                    </div>
                  );
                }
                return (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-semibold">
                    Within budget
                  </div>
                );
              })()}
            </div>
          )}

          {theme?.budget_guardrail?.warnings?.length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left">
              <p className="text-sm font-semibold text-amber-900">Planner check</p>
              <ul className="mt-1 space-y-1 text-sm text-amber-800">
                {theme.budget_guardrail.warnings.slice(0, 2).map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {hasUnestimatedUnpurchasedItems && (
            <div className="mt-4 pt-3 border-t border-blue-200/60 text-xs font-semibold text-slate-500 text-left">
              * Excludes items marked 'Estimate needed'.
            </div>
          )}

        </div>

        {/* 1-Click Buy & Sourcing Banner */}
        <div className="mb-4 sm:mb-6">
          <button
            type="button"
            onClick={() => setShowBulkCartModal(true)}
            className="w-full p-3.5 sm:p-4 rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white font-extrabold text-xs sm:text-sm shadow-md hover:shadow-lg transition flex items-center justify-between gap-3 active:scale-[0.99] group"
          >
            <div className="flex items-center gap-2.5 sm:gap-3 text-left">
              <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-white/20 flex items-center justify-center text-base sm:text-lg shrink-0">
                🛒
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-black tracking-tight text-white text-sm sm:text-base">1-Click Sourcing & Cart Exporter</span>
                  <span className="bg-white/30 text-white text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full">New</span>
                </div>
                <p className="text-[11px] sm:text-xs text-white/90 font-medium">Bundle items for Amazon, Target, or print a store-ready checklist</p>
              </div>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1 font-black text-xs bg-white text-stone-900 px-3 py-1.5 rounded-xl group-hover:bg-stone-100 transition shadow-sm">
              Export Items →
            </span>
          </button>
        </div>

        {/* Action Buttons - responsive sizing */}
        {canEditShoppingList && (
          <div className="grid grid-cols-3 gap-2 mb-6 sm:mb-8 lg:flex lg:flex-row lg:flex-wrap lg:gap-3">
            <button
              onClick={() => setShowAddItemModal(true)}
              className="w-full lg:flex-1 lg:max-w-none px-2 sm:px-4 py-2.5 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition flex items-center justify-center gap-1.5 text-xs sm:text-sm"
            >
              <span className="lg:hidden">+ Item</span>
              <span className="hidden lg:inline">+ Add Item</span>
            </button>
            <button
              onClick={() => {
                setCatalogLaunch({ initialTab: lastCatalogTab, initialCategory: lastCatalogCategory, initialSearch: '' });
                setShowCatalogModal(true);
              }}
              className="w-full lg:flex-1 lg:max-w-none px-2 sm:px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-800 font-semibold rounded-lg hover:border-primary hover:text-primary transition flex items-center justify-center gap-1.5 text-xs sm:text-sm"
            >
              <svg className="w-4 h-4 shrink-0 hidden lg:inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="lg:hidden">🔍 Catalog</span>
              <span className="hidden lg:inline">Add from Catalog</span>
            </button>
            <button
              onClick={() => setShowAddCategoryModal(true)}
              className="w-full lg:flex-1 lg:max-w-none px-2 sm:px-4 py-2.5 bg-white border-2 border-primary text-primary font-semibold rounded-lg hover:bg-primary hover:text-white transition flex items-center justify-center gap-1.5 text-xs sm:text-sm"
            >
              <span className="lg:hidden">+ Category</span>
              <span className="hidden lg:inline">+ Add Category</span>
            </button>
          </div>
        )}

        {/* Categories Section Header with Expand/Collapse All */}
        {allCategories.length > 0 && (
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Categories</h2>
            <button
              onClick={handleToggleAll}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-1.5 sm:gap-2"
              title={allExpanded ? 'Collapse all categories' : 'Expand all categories'}
            >
              {allExpanded ? (
                <>
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                  <span className="hidden sm:inline">Collapse All</span>
                  <span className="sm:hidden">Collapse</span>
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  <span className="hidden sm:inline">Expand All</span>
                  <span className="sm:hidden">Expand</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Curated Catalog Options Section */}
        {canEditShoppingList && showCuratedOptions && (
          <CuratedOptions 
            theme={theme} 
            eventData={eventData} 
            onBrowseCatalog={() => {
              setCatalogLaunch({ initialTab: lastCatalogTab, initialCategory: lastCatalogCategory, initialSearch: '' });
              setShowCatalogModal(true);
            }}
            onAddItem={(catalogItem) => {
              setItems(prev => [...prev, catalogItem]);
              // Expand the category the item was added to
              setExpandedCategories(prev => ({
                ...prev,
                [catalogItem.category]: true
              }));
            }} 
          />
        )}

        {wantsCocktails && !hasBeverageItems && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-bold text-amber-900">Beverages selected — add drink supplies to your list</p>
            <p className="mt-1 text-sm text-amber-800">
              Your event includes beverages. Browse the catalog for juices, sodas, mixers, mocktails, cups, and garnish ideas.
            </p>
            <button
              type="button"
              onClick={() => {
                setCatalogLaunch({ initialTab: 'items', initialCategory: '', initialSearch: 'cocktail bar mocktail' });
                setShowCatalogModal(true);
              }}
              className="mt-3 h-10 rounded-xl bg-amber-600 px-4 text-sm font-bold text-white transition hover:bg-amber-700"
            >
              Browse drink options
            </button>
          </div>
        )}

        {/* Categories */}
        {allCategories.map((category) => {
          const categoryItems = groupedItems[category] || [];
          const unpurchasedCategoryItems = categoryItems.filter(i => !i.purchased);

          if (unpurchasedCategoryItems.length === 0 && !customCategories.includes(category)) {
            return null;
          }

          const totals = calculateCategoryTotals(unpurchasedCategoryItems);

          const isExpanded = expandedCategories[category] ?? false;

          return (
            <div key={category} className="mb-4">
              {/* Category Header - Clickable */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => setExpandedCategories(prev => ({
                  ...prev,
                  [category]: !prev[category]
                }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedCategories(prev => ({
                      ...prev,
                      [category]: !prev[category]
                    }));
                  }
                }}
                className={`w-full rounded-2xl border text-left transition-all duration-200 cursor-pointer p-4 sm:p-5 ${isExpanded
                  ? 'bg-white border-gray-300 shadow-sm'
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                {/* Mobile Layout (sm:hidden) */}
                <div className="flex flex-col gap-2.5 sm:hidden">
                  {/* Top Row: Emoji + Title (Left) & Estimate (Right) */}
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-lg">
                        {categoryEmojis[category] || '📦'}
                      </span>
                      <span className="text-base font-extrabold text-gray-950 min-w-0 break-words">
                        {category}
                      </span>
                    </div>

                    {/* Estimate (Right) */}
                    <div className="shrink-0 text-right">
                      {unpurchasedCategoryItems.length > 0 && (
                        <div className="text-sm font-bold text-green-600">
                          {category === 'Rentals' ? (
                            <span className="text-amber-700">{formatEstimatedPrice(totals.optimizedTotal)}</span>
                          ) : totals.hasRentable ? (
                            <span className="text-amber-700">{formatEstimatedPrice(totals.optimizedTotal)}</span>
                          ) : (
                            <span className="text-green-600">{formatEstimatedPrice(totals.buyTotal)}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bottom Row: Metadata & Badge (Left) & Actions (Right) */}
                  <div className="flex items-center justify-between border-t border-gray-100/70 pt-2.5 w-full">
                    <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                      <span className="text-xs font-semibold text-gray-500">
                        {unpurchasedCategoryItems.length} item{unpurchasedCategoryItems.length === 1 ? '' : 's'}
                      </span>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold ${getCategoryOriginMeta(category, categoryItems).className}`}>
                        {getCategoryOriginMeta(category, categoryItems).label}
                      </span>
                    </div>

                    {/* Actions: Add Button & Chevron side-by-side (Right) */}
                    <div className="flex items-center gap-2 shrink-0">
                      {canEditShoppingList && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setNewItem(prev => ({
                              ...prev,
                              category: category,
                              quantity: '1',
                              unit: ''
                            }));
                            setShowAddItemModal(true);
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-primary hover:bg-primary-dark text-white font-sans text-[10px] font-bold uppercase tracking-wider transition shadow-sm active:scale-[0.95] duration-150"
                          title={`Add item directly to ${category}`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M12 5v14M5 12h14" />
                          </svg>
                          <span>Add</span>
                        </button>
                      )}

                      <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-600 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Desktop Layout (hidden sm:flex) */}
                <div className="hidden sm:flex items-center justify-between gap-4 w-full">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-700 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M19 9l-7 7-7-7" />
                      </svg>
                    </span>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-xl">
                      {categoryEmojis[category] || '📦'}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-base font-extrabold text-gray-950 sm:text-lg flex items-center break-words whitespace-normal">
                        {category}
                        {canEditShoppingList && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setNewItem(prev => ({
                                ...prev,
                                category: category,
                                quantity: '1',
                                unit: ''
                              }));
                              setShowAddItemModal(true);
                            }}
                            className="ml-2.5 shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary hover:bg-primary-dark text-white font-sans text-[10px] font-bold uppercase tracking-wider transition shadow-sm active:scale-[0.95] duration-150"
                            title={`Add item directly to ${category}`}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M12 5v14M5 12h14" />
                            </svg>
                            <span>Add</span>
                          </button>
                        )}
                      </span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-2">
                        <span className="block text-xs font-semibold text-gray-500">
                          {unpurchasedCategoryItems.length} item{unpurchasedCategoryItems.length === 1 ? '' : 's'} to handle
                        </span>
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${getCategoryOriginMeta(category, categoryItems).className}`}>
                          {getCategoryOriginMeta(category, categoryItems).label}
                        </span>
                      </span>
                    </span>
                  </div>

                  <div className="shrink-0 text-right">
                    {unpurchasedCategoryItems.length > 0 && (
                      <div className="text-sm font-semibold mt-1">
                        {category === 'Rentals' ? (
                          <div className="flex flex-col items-end">
                            <span className="text-amber-700">Rent: {formatEstimatedPrice(totals.optimizedTotal)}</span>
                            <span className="text-green-600">Buy: {formatEstimatedPrice(totals.buyTotal)}</span>
                          </div>
                        ) : totals.hasRentable ? (
                          <div className="flex flex-col items-end">
                            <span className="text-amber-700">Rent-optimized: {formatEstimatedPrice(totals.optimizedTotal)}</span>
                            <span className="text-green-600">Buy: {formatEstimatedPrice(totals.buyTotal)}</span>
                          </div>
                        ) : (
                          <span className="text-green-600">{formatEstimatedPrice(totals.buyTotal)}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Category Items - Collapsible */}
              {/* Always render items but hide when collapsed - ensures images load */}
              <div className={`bg-white border border-gray-200 rounded-2xl p-3 space-y-3 mt-2 sm:p-4 ${isExpanded ? '' : 'hidden'}`}>
                {canEditShoppingList && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => openCatalogForCategory(category)}
                      className="inline-flex h-9 items-center rounded-lg border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 transition hover:border-primary/40 hover:text-primary"
                    >
                      + Quick add from {category === 'Activities' ? 'activities' : 'catalog'}
                    </button>
                  </div>
                )}
                {unpurchasedCategoryItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white border border-gray-200 rounded-2xl p-4 hover:border-gray-300 hover:shadow-sm transition sm:p-5"
                  >
                    {/* Content */}
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={item.purchased}
                        onChange={() => handleTogglePurchased(item.id)}
                        disabled={!canEditShoppingList}
                        className={`w-5 h-5 mt-0.5 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary flex-shrink-0 ${canEditShoppingList ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                      />

                      {/* Item Content */}
                      <div className="flex-1 min-w-0">
                        {(() => {
                          const displayName = item.catalog_type === 'activity_supply' || item.category === 'Activity Supplies'
                            ? toTitleCaseLabel(item.name)
                            : item.name;
                          const linkedActivities = getLinkedActivities(item);
                          const isActivitySupply = item.catalog_type === 'activity_supply' || item.category === 'Activity Supplies';
                          const hideDuplicateDescription = isActivitySupply && linkedActivities.length > 0
                            && String(item.description || '').trim() === `Needed for ${linkedActivities.map((activity) => activity.name).filter(Boolean).join(', ')}.`;
                          const originBadge = getItemOriginBadge(item, themeCategories);
                          return (
                            <>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                          <span className="text-base sm:text-lg font-bold text-gray-900 break-words whitespace-normal">
                            {displayName}
                          </span>
                          <div className="flex items-center gap-2 shrink-0 sm:self-center">
                            {/* Activity companion supplies: show included-estimate label instead of a price badge.
                                The cost is already rolled into the parent activity's estimate — no separate price exists. */}
                            {isActivitySupply ? (
                              <span className="text-xs font-semibold text-stone-700 bg-stone-100 px-2 py-1 rounded-full whitespace-nowrap ring-1 ring-stone-200">
                                {item.estimate_label || 'Included in activity estimate'}
                              </span>
                            ) : (
                              <>
                                {item.buy_price && (
                                  <span className="text-sm font-bold text-green-600 bg-green-50 px-2 py-1 rounded whitespace-nowrap">
                                    {displayEstimatedPrice(item.buy_price, item)}
                                  </span>
                                )}
                                {item.rentable && item.rent_price && (
                                  <span className="text-sm font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded whitespace-nowrap">
                                    {displayEstimatedPrice(item.rent_price, item)}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3 mb-3">
                          {/* Inline Quantity Controls */}
                          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200 shrink-0">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const current = item.quantity || 1;
                                if (current > 1) {
                                  updateItemQuantity(item, current - 1);
                                }
                              }}
                              disabled={!canEditShoppingList || (item.quantity || 1) <= 1}
                              className="w-7 h-7 flex items-center justify-center bg-white rounded-md text-gray-600 font-bold hover:bg-gray-50 border border-gray-200/60 shadow-sm disabled:opacity-40 disabled:hover:bg-white transition"
                              title="Decrease quantity"
                            >
                              −
                            </button>

                            <span
                              onClick={canEditShoppingList ? () => handleQuantityEdit(item) : undefined}
                              className={`px-3 text-sm font-bold text-gray-800 ${canEditShoppingList ? 'cursor-pointer hover:underline' : ''}`}
                              title={canEditShoppingList ? 'Click to type quantity' : ''}
                            >
                              Qty {item.quantity || 1}{item.unit ? ` ${item.unit}` : ''}
                            </span>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const current = item.quantity || 1;
                                updateItemQuantity(item, current + 1);
                              }}
                              disabled={!canEditShoppingList}
                              className="w-7 h-7 flex items-center justify-center bg-white rounded-md text-gray-600 font-bold hover:bg-gray-50 border border-gray-200/60 shadow-sm disabled:opacity-40 transition"
                              title="Increase quantity"
                            >
                              +
                            </button>
                          </div>

                          {/* Metadata badges row */}
                          <div className="flex flex-wrap items-center gap-1.5">
                            {/* Color Tag */}
                            {item.color_tag && (
                              <span
                                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap"
                                style={{
                                  backgroundColor: item.color_tag,
                                  color: getReadableTextColor(item.color_tag)
                                }}
                              >
                                {getColorNameFromPalette(item.color_tag, theme.color_palette)}
                              </span>
                            )}
                            {/* Food Type Badge */}
                            {item.food_type && (
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${item.food_type === 'buy' ? 'bg-green-100 text-green-700 border border-green-200' :
                                'bg-orange-100 text-orange-700 border border-orange-200'
                                }`}>
                                {item.food_type === 'buy' ? 'Buy' : 'Cater'}
                              </span>
                            )}
                            {/* Meal Type Badge */}
                            {(item.meal_type || item.category === 'Food & Cake') && getMealType(item) !== 'Other' && (
                              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-stone-100 text-stone-700 border border-stone-200">
                                {getMealType(item)}
                              </span>
                            )}
                            {item.isCustom && (
                              <span className="text-xs bg-blue-100 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full font-semibold">
                                Custom
                              </span>
                            )}
                            {item.isPriceEdited && (
                              <span className="text-xs bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full font-semibold">
                                Custom price
                              </span>
                            )}
                            {!isActivitySupply && originBadge && (
                              <span className="text-xs bg-gray-100 text-gray-700 border border-gray-200 px-2.5 py-0.5 rounded-full font-semibold">
                                {originBadge}
                              </span>
                            )}
                            {isActivitySupply && item.supply_usage_type && (
                              <span className="text-xs bg-gray-100 text-gray-600 border border-gray-200 px-2.5 py-0.5 rounded-full capitalize font-semibold">
                                {item.supply_usage_type} supply
                              </span>
                            )}
                          </div>
                        </div>

                        {isActivitySupply && linkedActivities.length > 0 && (
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold text-gray-500">Needed for:</span>
                            {linkedActivities
                              .slice()
                              .sort((left, right) => String(left.name || left.key).localeCompare(String(right.name || right.key)))
                              .map((activity) => (
                                <span
                                  key={activity.key || activity.name}
                                  className={`inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-xs font-semibold leading-tight ${getActivityPillClass(activity.key || activity.name)}`}
                                >
                                  <span className="whitespace-normal break-words">
                                    {activity.name || activity.key}
                                  </span>
                                </span>
                              ))}
                          </div>
                        )}

                        {!hideDuplicateDescription && (
                          <div className="text-sm text-gray-600 mb-3">
                            {item.description}
                          </div>
                        )}
                            </>
                          );
                        })()}

                        {/* Organized Game Info */}
                        {item.game_type === 'organized' && (
                          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="text-xs font-semibold text-blue-800 mb-2">🎮 Organized Game:</div>
                            {item.game_instructions && (
                              <div className="text-sm text-blue-700 mb-2">
                                <span className="font-medium">How to play</span>
                                <ol className="mt-1 space-y-1">
                                  {parseInstructionSteps(item.game_instructions).map((step, index) => (
                                    <li key={`${item.id}-instruction-${index}`} className="grid grid-cols-[1.25rem_1fr] gap-1">
                                      <span className="font-semibold text-blue-500">{index + 1}.</span>
                                      <span>{step}</span>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            )}
                            {item.game_link && (
                              <a
                                href={item.game_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800 font-semibold underline"
                              >
                                📖 View detailed rules & tutorial →
                              </a>
                            )}
                          </div>
                        )}

                        {/* Shortlisted Links */}
                        {item.shortlistedLinks && item.shortlistedLinks.length > 0 && (
                          <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="text-xs font-semibold text-yellow-800 mb-2">📌 Shortlisted:</div>
                            <div className="flex flex-wrap gap-2">
                              {item.shortlistedLinks.map((link, idx) => (
                                <div key={idx} className="flex items-center bg-white border border-yellow-300 rounded px-2 py-1">
                                  <a
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline mr-2"
                                  >
                                    {link.label || 'Link'}
                                  </a>
                                  {canEditShoppingList && (
                                    <button
                                      onClick={() => handleRemoveLink(item.id, idx)}
                                      className="text-red-400 hover:text-red-600 text-xs"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Action Buttons - grid layout for better organization */}
                        <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-2 w-full">
                          {/* Primary: Buy/Order (only show for buyable items, not organized games) */}
                          {item.game_type !== 'organized' && (item.food_type === 'buy' || (!item.food_type && item.buy_price)) && (
                            <>
                              <button
                                onClick={() => handleBuy(item, 'online')}
                                className="w-full sm:w-auto inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-emerald-600 px-3 text-xs font-bold text-white transition hover:bg-emerald-700 active:scale-[0.98] duration-150"
                              >
                                <span>🛒</span>
                                <span>Find Online</span>
                              </button>
                              <button
                                onClick={() => handleBuy(item, 'store')}
                                className="w-full sm:w-auto inline-flex h-9 items-center justify-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 text-xs font-bold text-emerald-800 transition hover:bg-emerald-100 active:scale-[0.98] duration-150"
                              >
                                <span>🏪</span>
                                <span>Find In-Store</span>
                              </button>
                            </>
                          )}

                          {item.food_type === 'order' && (() => {
                            const vendor = getVendorHintForItem(item);
                            return (
                              <button
                                onClick={() => handleOrderFromVendor(item)}
                                className="w-full sm:w-auto inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-orange-500 px-3 text-xs font-bold text-white transition hover:bg-orange-600 active:scale-[0.98] duration-150"
                              >
                                <span>{vendor.emoji}</span>
                                <span>{vendor.label}</span>
                              </button>
                            );
                          })()}

                          {/* Rent */}
                          {item.rentable && (
                            <button
                              onClick={() => handleRent(item)}
                              className="w-full sm:w-auto inline-flex h-9 items-center justify-center rounded-full bg-indigo-600 px-3 text-xs font-bold text-white transition hover:bg-indigo-700 active:scale-[0.98] duration-150"
                            >
                              📦 Rent
                            </button>
                          )}

                          {/* Price - only if can edit */}
                          {canEditShoppingList && (
                            <button
                              type="button"
                              aria-label="Edit price"
                              onClick={() => openEditPrice(item)}
                              className="w-full sm:w-auto inline-flex h-9 items-center justify-center rounded-full border border-gray-200 bg-white px-3 text-xs font-bold text-gray-700 transition hover:bg-gray-50 active:scale-[0.98] duration-150"
                            >
                              ✏️ Price
                            </button>
                          )}

                          {/* Link - dynamic label - only if can edit */}
                          {canEditShoppingList && (
                            <button
                              onClick={() => {
                                setSelectedItemForLink(item);
                                setShowAddLinkModal(true);
                              }}
                              className="w-full sm:w-auto inline-flex h-9 items-center justify-center rounded-full bg-amber-500 px-3 text-xs font-bold text-white transition hover:bg-amber-600 active:scale-[0.98] duration-150"
                            >
                              {item.shortlistedLinks?.length > 0 ? '📎 Links' : '📎 Add'}
                            </button>
                          )}

                          {/* Replace - only if can edit */}
                          {canEditShoppingList && (
                            <button
                              onClick={() => handleReplace(item)}
                              className="w-full sm:w-auto inline-flex h-9 items-center justify-center rounded-full border border-gray-200 bg-white px-3 text-xs font-bold text-gray-700 transition hover:bg-gray-50 active:scale-[0.98] duration-150"
                            >
                              Replace
                            </button>
                          )}

                          {/* Remove - only if can edit */}
                          {canEditShoppingList && (
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="w-full sm:w-auto inline-flex h-9 items-center justify-center rounded-full border border-red-200 bg-white px-3 text-xs font-bold text-red-600 transition hover:bg-red-50 active:scale-[0.98] duration-150"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Empty Category Message */}
                {unpurchasedCategoryItems.length === 0 && (
                  <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                    No items in this category yet
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Empty Shopping List State */}
        {items.length === 0 && (
          <div className="text-center py-12 px-4 rounded-2xl border border-dashed border-gray-300 bg-white">
            <span className="text-4xl" role="img" aria-label="shopping cart">🛒</span>
            <h3 className="mt-3 text-lg font-bold text-gray-950">No shopping items yet</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
              Add a custom item or browse catalog picks to start building your checklist.
            </p>
            {canEditShoppingList && (
              <div className="mt-5 flex justify-center gap-3">
                <button
                  onClick={() => setShowAddItemModal(true)}
                  className="px-4 py-2 bg-primary text-white text-xs sm:text-sm font-bold rounded-lg hover:bg-primary-dark transition"
                >
                  + Add Item
                </button>
                <button
                  onClick={() => {
                    setCatalogLaunch({ initialTab: lastCatalogTab, initialCategory: lastCatalogCategory, initialSearch: '' });
                    setShowCatalogModal(true);
                  }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-800 text-xs sm:text-sm font-bold rounded-lg hover:bg-gray-50 transition"
                >
                  Browse Catalog
                </button>
              </div>
            )}
          </div>
        )}

        {/* Purchased Items Section */}
        {purchasedItems.length > 0 && (
          <div className="mt-8 border-t border-gray-200 pt-8">
            <div className="mb-4 rounded-2xl border border-emerald-200 bg-white px-5 py-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-extrabold uppercase tracking-[0.18em] text-emerald-700">Purchased items</div>
                  <h3 className="mt-1 text-xl font-bold text-gray-950">{purchasedItems.length} handled</h3>
                </div>
                <p className="max-w-md text-sm leading-6 text-gray-500">
                  Keep receipts, store notes, and real prices here so the final budget can become more accurate.
                </p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {purchasedItems.map((item) => (
                <div
                  key={item.id}
                  className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={true}
                      onChange={() => handleTogglePurchased(item.id)}
                      className="mt-1 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-emerald-400 text-emerald-600 focus:ring-2 focus:ring-emerald-500"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <h4 className="text-base font-bold text-gray-950">{item.name}</h4>
                          <p className="mt-1 text-sm text-gray-500">{item.description}</p>
                        </div>
                        {item.actual_price && (
                          <span className="w-fit rounded-full bg-emerald-50 px-3 py-1 text-sm font-bold text-emerald-700">
                            {item.actual_price}
                          </span>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-xs font-semibold text-gray-600">
                        <span className="rounded-full bg-gray-100 px-2.5 py-1">
                          {item.purchase_status === 'already_had' ? 'Already had' : 'Purchased'}
                        </span>
                        {item.purchase_source && (
                          <span className="rounded-full bg-gray-100 px-2.5 py-1">
                            {String(item.purchase_source).replace('_', ' ')}
                          </span>
                        )}
                        {item.purchase_vendor && (
                          <span className="rounded-full bg-gray-100 px-2.5 py-1">
                            {item.purchase_vendor}
                          </span>
                        )}
                      </div>
                      {item.purchase_notes && (
                        <p className="mt-3 rounded-xl bg-gray-50 px-3 py-2 text-sm text-gray-600">{item.purchase_notes}</p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedItemForPurchase(item);
                            setPurchaseForm({
                              status: item.purchase_status || 'purchased',
                              source: item.purchase_source || 'online',
                              vendor: item.purchase_vendor || '',
                              actualPrice: item.actual_price ? String(parsePrice(item.actual_price)) : '',
                              feedback: item.purchase_feedback || 'not_sure',
                              notes: item.purchase_notes || ''
                            });
                            setShowPurchaseModal(true);
                          }}
                          className="h-8 rounded-lg border border-gray-200 px-3 text-xs font-bold text-gray-700 hover:bg-gray-50"
                        >
                          Edit details
                        </button>
                        <button
                          type="button"
                          onClick={() => handleTogglePurchased(item.id)}
                          className="h-8 rounded-lg border border-gray-200 px-3 text-xs font-bold text-gray-700 hover:bg-gray-50"
                        >
                          Move back
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Subtle planning disclaimer */}
        <div className="mt-8 pt-6 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Suggestions and prices are planning estimates only. CraftMyParty does not sell items, process purchases, or guarantee availability or pricing.
          </p>
        </div>

        {/* Nav handled by TopStepperNav in App */}
      </div>

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999] p-3 sm:p-4" onClick={() => setShowAddItemModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-3.5 sm:p-6 animate-slide-up my-auto max-h-[calc(100dvh-2rem)] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-2 sm:mb-4 flex-shrink-0">
              <h3 className="text-lg sm:text-2xl font-bold text-gray-900">Add New Item</h3>
              <button
                onClick={() => setShowAddItemModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 transition text-xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-2.5 sm:space-y-3 overflow-y-auto flex-1 min-h-0 pr-1 pb-1">
              {/* Category */}
              <div>
                <label className="block text-[11px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                  Category *
                </label>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                  className="w-full h-[34px] sm:h-10 px-2.5 sm:px-3 bg-white border border-gray-300 rounded-md text-[13px] sm:text-sm text-gray-900 focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Select category</option>
                  {allCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Item Name */}
              <div>
                <label className="block text-[11px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                  Item Name *
                </label>
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  placeholder="e.g., LED String Lights"
                  className="w-full h-[34px] sm:h-10 px-2.5 sm:px-3 bg-white border border-gray-300 rounded-md text-[13px] sm:text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-[11px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                  Description
                </label>
                <textarea
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  placeholder="Brief description of the item"
                  rows={isMobile ? 1 : 2}
                  className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 bg-white border border-gray-300 rounded-md text-[13px] sm:text-sm text-gray-900 placeholder-gray-400 resize-none focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Quantity & Unit */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <label className="block text-[11px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newItem.quantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        setNewItem(prev => ({ ...prev, quantity: '' }));
                        return;
                      }
                      if (/^\d+$/.test(value)) {
                        setNewItem(prev => ({ ...prev, quantity: value }));
                      }
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    className="w-full h-[34px] sm:h-10 px-2.5 sm:px-3 bg-white border border-gray-300 rounded-md text-[13px] sm:text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  {!isValidQuantity(newItem.quantity) && (
                    <p className="mt-0.5 text-[10px] text-red-500 font-medium text-left">
                      Quantity must be 1 or more.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-[11px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                    Unit (optional)
                  </label>
                  <input
                    type="text"
                    value={newItem.unit}
                    onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                    placeholder="e.g., packs, trays"
                    className="w-full h-[34px] sm:h-10 px-2.5 sm:px-3 bg-white border border-gray-300 rounded-md text-[13px] sm:text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              {/* Purchase Type */}
              <div>
                <label className="block text-[11px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                  Purchase Type
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { value: 'buy_only', label: 'Buy Only' },
                    { value: 'rent_only', label: 'Rent Only' },
                    { value: 'buy_or_rent', label: 'Buy or Rent' }
                  ].map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setNewItem({ ...newItem, purchaseType: option.value })}
                      className={`px-1 py-1 sm:py-1.5 text-[9px] sm:text-xs font-semibold rounded-md border transition text-center whitespace-nowrap tracking-tight ${newItem.purchaseType === option.value
                        ? 'border-primary bg-primary text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-primary'
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Prices */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {newItem.purchaseType !== 'rent_only' && (
                  <div>
                    <label className="block text-[11px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                      Buy Price
                    </label>
                    <input
                      type="text"
                      value={newItem.buy_price}
                      onChange={(e) => setNewItem({ ...newItem, buy_price: e.target.value })}
                      placeholder="$50"
                      inputMode="decimal"
                      className="w-full h-[34px] sm:h-10 px-2.5 sm:px-3 bg-white border border-gray-300 rounded-md text-[13px] sm:text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                    {newItem.buy_price !== '' && !isValidPrice(newItem.buy_price) && (
                      <p className="mt-0.5 text-[10px] text-red-500 font-medium text-left">
                        Please enter a valid price (e.g., 49.99).
                      </p>
                    )}
                  </div>
                )}
                {newItem.purchaseType !== 'buy_only' && (
                  <div>
                    <label className="block text-[11px] sm:text-xs font-semibold text-gray-700 mb-0.5 sm:mb-1">
                      Rent Price
                    </label>
                    <input
                      type="text"
                      value={newItem.rent_price}
                      onChange={(e) => setNewItem({ ...newItem, rent_price: e.target.value })}
                      placeholder="$15"
                      inputMode="decimal"
                      className="w-full h-[34px] sm:h-10 px-2.5 sm:px-3 bg-white border border-gray-300 rounded-md text-[13px] sm:text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                    {newItem.rent_price !== '' && !isValidPrice(newItem.rent_price) && (
                      <p className="mt-0.5 text-[10px] text-red-500 font-medium text-left">
                        Please enter a valid price (e.g., 49.99).
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex gap-2.5 mt-4 sm:mt-6 flex-shrink-0">
              <button
                onClick={() => setShowAddItemModal(false)}
                className="flex-1 h-9 sm:h-10 bg-gray-100 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition text-[13px] sm:text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                disabled={!canAddNewItem}
                className="flex-1 h-9 sm:h-10 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed text-[13px] sm:text-sm"
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto flex items-start sm:items-center justify-center z-50 p-3 sm:p-4" onClick={() => setShowAddCategoryModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-slide-up my-4 sm:my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Add New Category</h3>
              <button
                onClick={() => setShowAddCategoryModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 transition text-xl"
              >
                ×
              </button>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category Name *
              </label>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="e.g., Party Favors"
                className="w-full h-11 px-4 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowAddCategoryModal(false)}
                className="flex-1 h-12 bg-gray-100 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCategory}
                className="flex-1 h-12 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition"
              >
                Add Category
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Link Modal */}
      {showEditPriceModal && selectedItemForPriceEdit && (
        <EditPriceModal
          item={selectedItemForPriceEdit}
          onClose={() => {
            setShowEditPriceModal(false);
            setSelectedItemForPriceEdit(null);
            setSelectedItemIdForPriceEdit(null);
          }}
          onSave={applyPriceEdit}
        />
      )}

      {/* Store Options Modal */}
      {showStoreOptionsModal && selectedItemForStore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto flex items-start sm:items-center justify-center z-50 p-3 sm:p-4" onClick={() => setShowStoreOptionsModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-xl w-full p-6 my-4 sm:my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Find {selectedItemForStore.name}</h3>
                <p className="text-gray-600">How would you like to find this item?</p>
              </div>
              <ModalCloseButton
                onClick={() => {
                  setShowStoreOptionsModal(false);
                  setSelectedItemForStore(null);
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => handleStoreOption('ready')}
                className="px-3 py-3 bg-blue-50 text-blue-900 font-semibold rounded-lg hover:bg-blue-100 transition text-left border border-blue-200"
              >
                <div className="font-bold text-base mb-2">Pre-Made</div>
                <div className="text-xs text-gray-700 leading-relaxed">Find at physical retail stores and party supply shops near you</div>
              </button>

              <button
                onClick={() => handleStoreOption('custom')}
                className="px-3 py-3 bg-stone-50 text-stone-900 font-semibold rounded-lg hover:bg-stone-100 transition text-left border border-stone-200"
              >
                <div className="font-bold text-base mb-2">Custom</div>
                <div className="text-xs text-gray-700 leading-relaxed">Find customization services and print shops for personalized options</div>
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Buy Online Options Modal */}
      {showBuyOnlineModal && selectedItemForBuyOnline && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto flex items-start sm:items-center justify-center z-50 p-3 sm:p-4" onClick={() => setShowBuyOnlineModal(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-xl w-full p-6 my-4 sm:my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Find {selectedItemForBuyOnline.name}</h3>
                <p className="text-gray-600">We guide you to options — you compare and purchase on your own.</p>
              </div>
              <ModalCloseButton
                onClick={() => {
                  setShowBuyOnlineModal(false);
                  setSelectedItemForBuyOnline(null);
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => handleBuyOnlineOption('google')}
                className="px-4 py-4 bg-blue-50 text-blue-900 font-semibold rounded-lg hover:bg-blue-100 transition border border-blue-200 flex flex-col items-center text-center"
              >
                {/* Row 1: Large Icon */}
                <div className="mb-3">
                  <img
                    src="/google-shopping-icon.png"
                    alt="Google Search"
                    className="w-12 h-12 object-contain"
                    onError={(e) => {
                      // Fallback to SVG if image not found
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                  <svg className="w-12 h-12 hidden" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 8L6 12L6 20C6 21.1 6.9 22 8 22L16 22C17.1 22 18 21.1 18 20L18 12L16 8L8 8Z" fill="#4285F4" />
                    <path d="M16 8L18 12L18 20C18 21.1 17.1 22 16 22L16 8Z" fill="#34A853" />
                    <path d="M6 12L8 8L8 22L6 20L6 12Z" fill="#EA4335" />
                    <path d="M8 8L16 8L18 12L6 12L8 8Z" fill="#FBBC05" />
                    <path d="M8 6C8 4.9 8.9 4 10 4L14 4C15.1 4 16 4.9 16 6L16 8L8 8L8 6Z" fill="#4285F4" />
                  </svg>
                </div>
                {/* Row 2: Text */}
                <div className="font-bold text-base mb-2">Google Search</div>
                {/* Row 3: Subtext */}
                <div className="text-xs text-gray-700 leading-relaxed">Search broadly across retailers, marketplaces, and local options</div>
              </button>

              <button
                onClick={() => handleBuyOnlineOption('amazon')}
                className="px-4 py-4 bg-orange-50 text-orange-900 font-semibold rounded-lg hover:bg-orange-100 transition border border-orange-200 flex flex-col items-center text-center"
              >
                {/* Row 1: Large Icon */}
                <div className="mb-3">
                  <img
                    src="/amazon-icon.png"
                    alt="Amazon"
                    className="w-12 h-12 object-contain"
                    onError={(e) => {
                      // Fallback to SVG if image not found
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                  <svg className="w-12 h-12 hidden" viewBox="0 0 200 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 50C0 50 100 10 100 10C100 10 200 50 200 50L180 60L100 20L20 60L0 50Z" fill="#FF9900" />
                  </svg>
                </div>
                {/* Row 2: Text */}
                <div className="font-bold text-base mb-2">Amazon</div>
                {/* Row 3: Subtext */}
                <div className="text-xs text-gray-700 leading-relaxed">Shop on Amazon {getAmazonDomain(country).replace('amazon.', '')}</div>
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Food Sourcing Modal */}
      {showFoodSourcingModal && selectedFoodItem && (
        <div className="fixed inset-0 z-50 overflow-y-auto flex items-start sm:items-center justify-center bg-black/50 p-3 sm:p-4" onClick={() => setShowFoodSourcingModal(false)}>
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl my-4 sm:my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-950">Source {getItemDisplayName(selectedFoodItem)}</h3>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  Food works better through nearby restaurants, bakeries, caterers, or delivery searches than general retail links.
                </p>
              </div>
              <ModalCloseButton
                onClick={() => {
                  setShowFoodSourcingModal(false);
                  setSelectedFoodItem(null);
                }}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {getFoodSourcingOptions(selectedFoodItem).map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleFoodSourcingOption(option.id)}
                  className={`rounded-2xl border p-4 text-left transition ${
                    option.tone === 'orange' ? 'border-orange-200 bg-orange-50 hover:bg-orange-100' :
                    option.tone === 'pink' ? 'border-pink-200 bg-pink-50 hover:bg-pink-100' :
                    option.tone === 'blue' ? 'border-blue-200 bg-blue-50 hover:bg-blue-100' :
                    option.tone === 'gray' ? 'border-gray-200 bg-white hover:bg-gray-50' :
                    'border-emerald-200 bg-emerald-50 hover:bg-emerald-100'
                  }`}
                >
                  <div className="text-base font-bold text-gray-950">{option.title}</div>
                  <div className="mt-1 text-sm text-gray-700">{option.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Purchase Details Modal */}
      {showPurchaseModal && selectedItemForPurchase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4" onClick={() => setShowPurchaseModal(false)}>
          <div className="w-full max-w-xl rounded-2xl bg-white p-4 sm:p-6 shadow-2xl my-auto max-h-[calc(100dvh-2rem)] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 sm:mb-5 flex items-start justify-between gap-4 flex-shrink-0">
              <div>
                <h3 className="text-lg sm:text-2xl font-bold text-gray-950">Mark as handled</h3>
                <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-gray-600">{selectedItemForPurchase.name}</p>
              </div>
              <ModalCloseButton
                onClick={() => {
                  setShowPurchaseModal(false);
                  setSelectedItemForPurchase(null);
                }}
              />
            </div>

            <div className="space-y-2.5 sm:space-y-4 overflow-y-auto flex-1 min-h-0 pr-1 pb-1">
              <div>
                <label className="mb-1 block text-xs sm:text-sm font-bold text-gray-700">Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    ['purchased', 'Purchased'],
                    ['already_had', 'Already had it']
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setPurchaseForm(prev => ({ ...prev, status: value }))}
                      className={`h-9 sm:h-11 rounded-xl border text-xs sm:text-sm font-bold ${purchaseForm.status === value ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                <div>
                  <label className="mb-1 block text-xs sm:text-sm font-bold text-gray-700">Source</label>
                  <select
                    value={purchaseForm.source}
                    onChange={(e) => setPurchaseForm(prev => ({ ...prev, source: e.target.value }))}
                    className="h-9 sm:h-11 w-full rounded-xl border border-gray-300 px-3 text-xs sm:text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="online">Online</option>
                    <option value="in_store">In store</option>
                    <option value="local_vendor">Local vendor</option>
                    <option value="rental">Rental</option>
                    <option value="already_owned">Already owned</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs sm:text-sm font-bold text-gray-700">Actual cost</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={purchaseForm.actualPrice}
                    onChange={(e) => setPurchaseForm(prev => ({ ...prev, actualPrice: e.target.value }))}
                    placeholder="e.g., 42.50"
                    className="h-9 sm:h-11 w-full rounded-xl border border-gray-300 px-3 text-xs sm:text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                <div>
                  <label className="mb-1 block text-xs sm:text-sm font-bold text-gray-700">Store or vendor</label>
                  <input
                    type="text"
                    value={purchaseForm.vendor}
                    onChange={(e) => setPurchaseForm(prev => ({ ...prev, vendor: e.target.value }))}
                    placeholder="e.g., Trader Joe's"
                    className="h-9 sm:h-11 w-full rounded-xl border border-gray-300 px-3 text-xs sm:text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs sm:text-sm font-bold text-gray-700">How did it work out?</label>
                  <select
                    value={purchaseForm.feedback}
                    onChange={(e) => setPurchaseForm(prev => ({ ...prev, feedback: e.target.value }))}
                    className="h-9 sm:h-11 w-full rounded-xl border border-gray-300 px-3 text-xs sm:text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="liked">Liked it</option>
                    <option value="not_liked">Would not use again</option>
                    <option value="not_sure">Not sure yet</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs sm:text-sm font-bold text-gray-700">Notes</label>
                <textarea
                  value={purchaseForm.notes}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional receipt, quantity, delivery, or quality notes"
                  rows={2}
                  className="w-full rounded-xl border border-gray-300 px-3 py-1.5 text-xs sm:text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            <div className="mt-4 sm:mt-6 flex flex-row justify-end gap-2.5 sm:gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  setShowPurchaseModal(false);
                  setSelectedItemForPurchase(null);
                }}
                className="flex-1 sm:flex-none h-9 sm:h-11 rounded-xl border border-gray-200 px-4 sm:px-5 text-xs sm:text-sm font-bold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSavePurchaseDetails}
                className="flex-1 sm:flex-none h-9 sm:h-11 rounded-xl bg-emerald-600 px-4 sm:px-5 text-xs sm:text-sm font-bold text-white hover:bg-emerald-700"
              >
                Save as handled
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddLinkModal && selectedItemForLink && (
        <div className="fixed inset-0 bg-black bg-opacity-60 overflow-y-auto flex items-start sm:items-center justify-center z-50 p-3 sm:p-4" onClick={() => setShowAddLinkModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-slide-up my-4 sm:my-auto max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {selectedItemForLink.shortlistedLinks?.length > 0 ? 'Manage Links' : 'Add Link'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">for: {selectedItemForLink.name}</p>
              </div>
              <ModalCloseButton
                onClick={() => {
                  setShowAddLinkModal(false);
                  setSelectedItemForLink(null);
                }}
                className="h-9 px-3 text-xs"
              />
            </div>

            {/* Existing Links */}
            {selectedItemForLink.shortlistedLinks?.length > 0 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="text-sm font-semibold text-yellow-800 mb-3">📌 Saved Links</div>
                <div className="space-y-2">
                  {selectedItemForLink.shortlistedLinks.map((link, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white border border-yellow-300 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-700 whitespace-normal break-words flex-1 mr-2">{link.label || link.url}</span>
                      <div className="flex items-center gap-2">
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                        >
                          Open
                        </a>
                        <button
                          onClick={() => handleRemoveLink(selectedItemForLink.id, idx)}
                          className="text-xs text-red-500 hover:text-red-700 font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add New Link */}
            <div className="space-y-4">
              <div className="text-sm font-semibold text-gray-700">Add New Link</div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">URL *</label>
                <input
                  type="url"
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  placeholder="https://..."
                  className="w-full h-11 px-4 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Label (optional)</label>
                <input
                  type="text"
                  value={newLink.label}
                  onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                  placeholder="e.g., Amazon, Etsy"
                  className="w-full h-11 px-4 bg-white border border-gray-300 rounded-md text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddLinkModal(false);
                  setSelectedItemForLink(null);
                }}
                className="flex-1 h-11 bg-gray-100 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAddLink(false)}
                className="flex-1 h-11 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition"
              >
                Add another
              </button>
              <button
                onClick={() => handleAddLink(true)}
                className="flex-1 h-11 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quantity Edit Modal */}
      {showQuantityModal && quantityModalItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto flex items-start sm:items-center justify-center z-50 p-3 sm:p-4" onClick={handleQuantityModalClose}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 animate-slide-up my-4 sm:my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Edit Quantity</h3>
                <p className="text-sm text-gray-600">{quantityModalItem.name}</p>
              </div>
              <ModalCloseButton onClick={handleQuantityModalClose} className="h-9 px-3 text-xs" />
            </div>

            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                onClick={handleQuantityDecrease}
                disabled={parseInt(quantityInput, 10) <= 1}
                className="w-12 h-12 flex items-center justify-center bg-gray-100 border-2 border-gray-300 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition font-bold text-gray-700 text-xl"
              >
                −
              </button>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={quantityInput}
                onChange={(e) => {
                  const val = e.target.value;
                  const numeric = val.replace(/[^0-9]/g, '');
                  setQuantityInput(numeric);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleQuantitySave();
                  } else if (e.key === 'Escape') {
                    handleQuantityModalClose();
                  }
                }}
                className="w-24 h-12 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                autoFocus
              />

              <button
                onClick={handleQuantityIncrease}
                className="w-12 h-12 flex items-center justify-center bg-gray-100 border-2 border-gray-300 rounded-lg hover:bg-gray-200 transition font-bold text-gray-700 text-xl"
              >
                +
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleQuantityModalClose}
                className="flex-1 h-11 bg-gray-100 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleQuantitySave}
                className="flex-1 h-11 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Catalog Explorer Modal */}
	      {showCatalogModal && (
	        <CatalogExplorer
	          isOpen={showCatalogModal}
          onClose={() => setShowCatalogModal(false)}
          onSelectItem={handleSelectItemFromCatalog}
          eventData={eventData}
          existingItems={items}
          initialTab={catalogLaunch.initialTab}
          initialCategory={catalogLaunch.initialCategory}
          initialSearch={catalogLaunch.initialSearch}
          onTabChange={setLastCatalogTab}
          onCategoryChange={setLastCatalogCategory}
	        />
	      )}
	      {pendingRemoveItemId && (
	        <div
	          className="fixed inset-0 z-[70] overflow-y-auto flex items-start sm:items-center justify-center bg-black/50 p-3 sm:p-4 backdrop-blur-sm"
	          onClick={() => {
              setPendingRemoveItemId(null);
              setPendingRemoveLinkedSupplies([]);
            }}
	        >
	          <div
	            className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl my-4 sm:my-auto"
	            onClick={(e) => e.stopPropagation()}
	          >
	            {(() => {
                const pendingItem = items.find(item => item.id === pendingRemoveItemId);
                const linkedCount = pendingRemoveLinkedSupplies.length;
                const linkedPreview = pendingRemoveLinkedSupplies.slice(0, 5);
                return (
                  <>
                    <h3 className="text-lg font-extrabold text-gray-950">
                      {pendingItem?.name ? `Remove ${pendingItem.name}?` : 'Remove this item?'}
                    </h3>
                    {linkedCount > 0 ? (
                      <>
                        <p className="mt-2 text-sm leading-6 text-gray-600">
                          This will also remove {linkedCount} linked supply item{linkedCount === 1 ? '' : 's'}:
                        </p>
                        <ul className="mt-3 space-y-1 text-sm text-gray-700">
                          {linkedPreview.map((supply) => (
                            <li key={supply.id} className="whitespace-normal break-words">
                              - {toTitleCaseLabel(supply.name)}
                            </li>
                          ))}
                          {linkedCount > 5 && (
                            <li className="text-gray-500">+ {linkedCount - 5} more</li>
                          )}
                        </ul>
                        <p className="mt-3 text-sm text-gray-500">
                          These supplies were added because they are needed for this activity.
                        </p>
                      </>
                    ) : (
                      <p className="mt-2 text-sm leading-6 text-gray-600">
                        This removes it from the current shopping list. You can add it again later from the catalog or by creating a custom item.
                      </p>
                    )}
                  </>
                );
              })()}
	            <div className="mt-5 flex justify-end gap-3">
	              <button
	                type="button"
	                onClick={() => {
                    setPendingRemoveItemId(null);
                    setPendingRemoveLinkedSupplies([]);
                  }}
	                className="h-10 rounded-xl bg-gray-100 px-4 text-sm font-bold text-gray-700 transition hover:bg-gray-200"
	              >
	                Cancel
	              </button>
	              <button
	                type="button"
	                onClick={confirmRemoveItem}
	                className="h-10 rounded-xl bg-red-600 px-4 text-sm font-bold text-white transition hover:bg-red-700"
	              >
	                Remove item
	              </button>
	            </div>
	          </div>
	        </div>
	      )}
        <BulkCartExportModal
          isOpen={showBulkCartModal}
          onClose={() => setShowBulkCartModal(false)}
          items={items}
          theme={theme}
          eventData={eventData}
        />
	    </>
	  );

  // Return wrapped or unwrapped based on inTab prop
  if (inTab) {
    return content;
  }

  return (
    <div className={outerWrapperClass} style={outerWrapperStyle}>
      {content}
    </div>
  );
};

export default ShoppingList;
